import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import config from '../config';
import { BarChart2, BarChart3, RefreshCw, Settings, Plus, Package, Database, Lock, LayoutDashboard } from 'lucide-react';
import { iconeDashboard } from '../constants/iconesDashboard';
import { libelleRefusConnecteur } from '../utils/libelleRefusConnecteur';
import googleAdsLogo from '../logos/google-ads.png';
import microsoftAdsLogo from '../logos/microsoft-ads.png';
import metaAdsLogo from '../logos/meta-ads.png';
import pinterestAdsLogo from '../logos/pinterest-ads.png';
import tiktokAdsLogo from '../logos/tiktok-ads.png';
import chatgptAdsLogo from '../logos/chatgpt-ads.png';
import awinLogo from '../logos/awin.png';
import pianoLogo from '../logos/piano-analytics.png';
import matomoLogo from '../logos/matomo.png';
import { useComponentMetrics, getMetricLabel } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import BulleSurvol from './BulleSurvol';

import { makeRenderVariation, DEPUIS_ZERO } from '../utils/variationUtils';
const API_URL = config.API_URL;

// Afficheur des variations de la page d'accueil : même sémantique que les
// tableaux des dashboards, avec la typographie propre à ces cartes.
const renderGrowthRate = makeRenderVariation({
  fontSize: '0.75rem',
  style: { fontWeight: '500' }
});

/**
 * Cellule vide : ni valeur à comparer, ni chiffre à lire.
 *
 * `valeur: null` n'est pas zéro. Un compte dont le budget n'est pas mesurable ne doit pas
 * se ranger devant celui qui a réellement dépensé zéro euro — d'où le tri qui rejette ces
 * lignes en fin de liste, dans les deux sens.
 */
const CELLULE_VIDE = { valeur: null, texte: '-', variation: null };

/**
 * La même cellule vide, pour une mesure que la solution d'analytics n'a pas rendue.
 *
 * Le cas vient de Matomo : un `Goals.get` abandonné au bout de 30 secondes — l'instance
 * archivait — remontait jusqu'ici en `0 commande, 0 €`, et l'accueil l'affichait comme un
 * constat, variation « −100 % » comprise. Le serveur rend désormais `null` ; la cellule reste
 * donc vide, exactement comme un budget non mesurable, et le survol dit pourquoi.
 *
 * ⚠️ Le tri rejette déjà les `valeur: null` en fin de liste, dans les deux sens : une mesure
 * absente ne doit pas se ranger devant une mesure réellement nulle.
 */
const cellulePasMesuree = (origine) => ({
  ...CELLULE_VIDE,
  infobulle: `Mesure non rendue par la solution d'analytics — ce n'est pas un zéro.`
    + (origine ? ` (${origine})` : '')
});

/** Clé de la colonne des libellés, commune aux trois tableaux. */
const COLONNE_NOM = '__nom';

/**
 * Classe des lignes sur une colonne.
 *
 * **Une valeur absente n'est pas une petite valeur.** Un compte dont le budget n'est pas
 * mesurable sort en dernier dans les deux sens, plutôt que de se ranger devant celui qui a
 * réellement dépensé zéro euro. Le tri est stable — `Array.prototype.sort` l'est — donc
 * deux lignes à égalité gardent l'ordre reçu du serveur.
 */
const trier = (lignes, tri, valeurDe) => [...(lignes || [])].sort((a, b) => {
  const va = valeurDe(a, tri.cle);
  const vb = valeurDe(b, tri.cle);

  if (typeof va === 'string' || typeof vb === 'string') {
    const ordre = String(va ?? '').localeCompare(String(vb ?? ''), 'fr');
    return tri.sens === 'asc' ? ordre : -ordre;
  }

  const aVide = va === null || va === undefined || Number.isNaN(va);
  const bVide = vb === null || vb === undefined || Number.isNaN(vb);
  if (aVide || bVide) return aVide && bVide ? 0 : (aVide ? 1 : -1);

  return tri.sens === 'asc' ? va - vb : vb - va;
});

/**
 * Bascule le tri sur une colonne.
 *
 * Recliquer la colonne courante inverse le sens ; en choisir une autre repart du sens le
 * plus utile pour elle — décroissant sur un chiffre, où l'on cherche le plus gros, mais
 * croissant sur les libellés, où l'on cherche un nom dans l'ordre alphabétique.
 *
 * La comparaison porte sur le tri **affiché** et non sur l'état brut : quand une colonne
 * masquée détient le tri, c'est la flèche que l'utilisateur voit qui doit s'inverser sous
 * son clic, sinon le premier clic semblerait sans effet.
 */
const basculerTri = (definirTri, cle, triCourant) => definirTri(
  triCourant.cle === cle
    ? { cle, sens: triCourant.sens === 'desc' ? 'asc' : 'desc' }
    : { cle, sens: cle === COLONNE_NOM ? 'asc' : 'desc' }
);

/**
 * En-tête cliquable.
 *
 * La flèche n'apparaît que sur la colonne active : trois flèches grises à l'écran ne
 * diraient plus laquelle commande le classement.
 */
const EnteteTriable = ({ tri, definirTri, cle, children, alignement = 'center', style = {} }) => (
  <th
    onClick={() => basculerTri(definirTri, cle, tri)}
    title="Trier sur cette colonne"
    style={{
      padding: '16px 8px',
      textAlign: alignement,
      fontWeight: '600',
      fontSize: '0.8rem',
      color: tri.cle === cle ? '#1f2937' : '#374151',
      borderBottom: '1px solid #e2e8f0',
      cursor: 'pointer',
      userSelect: 'none',
      ...style
    }}
  >
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      justifyContent: alignement === 'left' ? 'flex-start' : 'center'
    }}>
      {children}
      <span style={{ fontSize: '0.7rem', color: '#6b7280', visibility: tri.cle === cle ? 'visible' : 'hidden' }}>
        {tri.sens === 'asc' ? '▲' : '▼'}
      </span>
    </div>
  </th>
);

/**
 * Tri réellement appliqué, une fois confronté aux colonnes affichées.
 *
 * Retirer une colonne dans « Configurer les métriques » ne doit pas laisser le tableau
 * classé sur un critère devenu invisible : la première colonne restante reprend la main, et
 * la flèche se pose dessus. Sans ce garde-fou, l'ordre des lignes n'aurait plus d'explication
 * à l'écran — le pire des deux mondes, un classement qu'on subit sans pouvoir le lire.
 */
const triEffectif = (tri, colonnes) => (
  tri.cle === COLONNE_NOM || colonnes.includes(tri.cle)
    ? tri
    : { ...tri, cle: colonnes[0] || COLONNE_NOM }
);

/** Libellé d'une colonne de métrique, dans la forme courte propre à la page d'accueil. */
const libelleColonne = (metricKey) => (
  metricKey === 'cout' ? 'Budget' :
  metricKey === 'taux_conv' ? '% Conv.' :
  metricKey === 'conv' ? 'Conv.' :
  metricKey === 'valeur_conv' ? 'Valeur Conv.' :
  metricKey === 'panier_moyen' ? 'Panier moy.' :
  getMetricLabel(metricKey)
);

/** Base de comparaison retenue, côté navigateur — voir `preferenceComparaison` côté compte. */
const CLE_COMPARAISON = 'kokpit.baseComparaison';

/** Les deux bases que l'accueil sait calculer ; la même liste garde la route côté serveur. */
const BASES_COMPARAISON = ['previous_period', 'previous_year'];

const HomePage = ({ token, user, userSubscription, userOrganizations, selectedClientId, setCurrentView, setCurrentDashboardType, navigateToDashboardWithAccount, navigateToDiagnosticWithAccount, selectedPeriod, setSelectedPeriod, customStartDate, setCustomStartDate, customEndDate, setCustomEndDate, selectedSiteId, userSites = [], ouvrirChoixDashboard, globalConnectors = [], setOpenAddModalOnLibraryLoad, setOpenAddConnectorModalOnLoad, compareEnabled = false, comparisonType = 'previous_period', comparisonStartDate = '', comparisonEndDate = '',
  dashboardsSurMesure = [], ouvrirDashboardSurMesure }) => {
  /*
   * Un accès client ne connecte rien et n'ajoute aucun compte : il reçoit ce que son agence
   * lui ouvre. L'écran vide doit donc lui dire autre chose qu'aux autres — sans quoi il
   * propose un geste impossible, et c'est de l'outil qu'on conclut qu'il est cassé.
   */
  const estAccesClient = user?.role === 'CLIENT';

  // Vérifier si des connecteurs sont actifs (sources connectées)
  const hasActiveConnectors = globalConnectors.filter(c => c.isActive).length > 0;
  /**
   * La base de comparaison des trois tableaux de l'accueil, choisie dans le titre.
   *
   * Ces tableaux comparent toujours — le serveur calcule sa période de référence quoi qu'il
   * arrive. Seul son choix était figé : « période précédente », sans moyen de demander la
   * même période l'an passé, qui est pourtant la lecture attendue sur une activité
   * saisonnière.
   *
   * L'état s'initialise sur le réglage global de l'application, quand une comparaison y est
   * active : l'accueil ne doit pas contredire ce qui a été choisi ailleurs.
   */
  const [baseComparaison, setBaseComparaison] = useState(() => {
    // Un réglage de comparaison actif dans l'application l'emporte : l'accueil ne doit pas
    // contredire ce qui vient d'être choisi ailleurs.
    if (compareEnabled && comparisonType === 'previous_year') return 'previous_year';
    try {
      const locale = localStorage.getItem(CLE_COMPARAISON);
      if (BASES_COMPARAISON.includes(locale)) return locale;
    } catch (e) {
      // Navigation privée, stockage refusé : on ouvre sur le défaut, sans conséquence.
    }
    return 'previous_period';
  });

  /**
   * Ce qui a déjà été enregistré, pour ne pas réécrire ce qu'on vient de lire.
   *
   * Sans ce repère, appliquer la préférence du compte déclencherait l'effet d'enregistrement,
   * qui la renverrait au serveur telle quelle — un appel pour rien à chaque ouverture.
   */
  const comparaisonEnregistree = useRef(null);

  /**
   * La préférence du **compte** l'emporte sur celle du navigateur.
   *
   * Elle arrive avec l'utilisateur, `verifyToken` rendant la ligne entière : aucun appel
   * supplémentaire. Une base changée depuis un autre appareil doit se retrouver ici — c'est
   * tout l'objet de l'avoir mise en base.
   *
   * Le réglage global de l'application reste prioritaire : s'il porte une comparaison active,
   * c'est une décision prise à l'instant, pas une préférence d'hier.
   */
  useEffect(() => {
    const duCompte = user?.preferenceComparaison;
    if (!BASES_COMPARAISON.includes(duCompte)) return;
    if (compareEnabled && comparisonType === 'previous_year') return;
    comparaisonEnregistree.current = duCompte;
    setBaseComparaison(duCompte);
  }, [user?.preferenceComparaison, compareEnabled, comparisonType]);

  /**
   * Le choix, retenu des deux côtés.
   *
   * Le stockage local répond avant le premier rendu ; la base n'arrive qu'après
   * l'authentification. S'en remettre à elle seule ferait ouvrir l'accueil sur « période
   * précédente » puis basculer, en rechargeant au passage les trois tableaux.
   *
   * Un échec d'écriture est passé sous silence : perdre un réglage de confort n'empêche rien,
   * et une alerte serait plus gênante que l'oubli.
   */
  useEffect(() => {
    if (comparaisonEnregistree.current === baseComparaison) return;
    comparaisonEnregistree.current = baseComparaison;

    try {
      localStorage.setItem(CLE_COMPARAISON, baseComparaison);
    } catch (e) {
      // Sans conséquence : le choix vaut pour la session en cours.
    }

    if (!token) return;
    axios.put(
      `${config.API_URL}/api/preferences/comparaison`,
      { comparaison: baseComparaison },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(() => {});
  }, [baseComparaison, token]);

  /**
   * Le bandeau de chargement d'un tableau de la page.
   *
   * Les trois sections se chargent séparément et affichent la même attente : la recopier
   * laisserait dériver l'une d'elles au premier ajustement.
   */
  const bandeauDeChargement = (titre, detail) => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px',
      textAlign: 'center',
      padding: '40px',
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      marginBottom: '24px'
    }}>
      <div className="loading-spinner" />
      <h3 style={{ margin: '16px 0 8px 0' }}>{titre}</h3>
      <p style={{ margin: 0, color: '#64748b' }}>{detail}</p>
    </div>
  );

  const [loadingTableData, setLoadingTableData] = useState(true);
  const [error, setError] = useState('');

  const [showCustomDates, setShowCustomDates] = useState(selectedPeriod === 'CUSTOM');
  const [platformData, setPlatformData] = useState([]);
  const [expandedPlatforms, setExpandedPlatforms] = useState(new Set());
  const [platformAccountDetails, setPlatformAccountDetails] = useState({});
  const [refreshingCache, setRefreshingCache] = useState(false);

  /**
   * La mention survolée, et les bords de la cellule qui la porte.
   *
   * **Un `title` natif ne suffisait pas.** Il était bien posé — le curseur passait au point
   * d'interrogation — mais l'infobulle du navigateur ne sortait pas sur ces cellules. La bulle
   * maison, elle, est à nous : elle s'affiche à coup sûr, elle se lit, et c'est déjà le moyen
   * employé ailleurs dans l'application (tableau des placements Meta, listes de comptes).
   *
   * Les deux bords verticaux sont retenus : le bas pour poser la bulle dessous, le haut pour
   * qu'elle se retourne quand elle ne tient pas en bas d'écran — voir `BulleSurvol`.
   */
  const [mentionSurvolee, setMentionSurvolee] = useState(null);

  // États pour le tableau Analytics (Matomo, GA4, etc.)
  const [analyticsData, setAnalyticsData] = useState([]);
  const [expandedAnalyticsSources, setExpandedAnalyticsSources] = useState(new Set());
  const [loadingAnalyticsData, setLoadingAnalyticsData] = useState(true);

  /**
   * Tri de chaque tableau : la colonne, et le sens.
   *
   * Le défaut porte sur le volume — sessions pour les solutions de mesure, clics pour les
   * régies — et non sur le nom : ce qu'on cherche en ouvrant la page, c'est ce qui pèse,
   * pas la première lettre de l'alphabet. Décroissant, donc, pour que le plus gros soit
   * immédiatement à l'écran.
   *
   * Un tri par tableau et non un tri commun : les trois n'ont pas les mêmes colonnes, et
   * classer les Plateformes par sessions n'aurait aucun sens.
   */
  const [triAnalytics, setTriAnalytics] = useState({ cle: 'sessions', sens: 'desc' });
  const [triPlateformes, setTriPlateformes] = useState({ cle: 'clics', sens: 'desc' });

  // États pour la configuration des métriques
  const [showAnalyticsMetricsConfig, setShowAnalyticsMetricsConfig] = useState(false);
  const [showPlatformsMetricsConfig, setShowPlatformsMetricsConfig] = useState(false);

  // Hooks pour les métriques configurables
  const analyticsMetricsConfig = useComponentMetrics('homepage_analytics', 'global');
  const platformsMetricsConfig = useComponentMetrics('homepage_platforms', 'global');

  // Fonction pour rafraîchir le cache des recommandations (local uniquement)
  const handleRefreshCache = async () => {
    if (refreshingCache) return;
    setRefreshingCache(true);
    try {
      await axios.post(`${API_URL}/api/recommendations/refresh`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Recalcul du cache lancé en arrière-plan. Patientez quelques secondes puis rafraîchissez la page.');
    } catch (err) {
      console.error('Erreur refresh cache:', err);
      alert('Erreur lors du refresh: ' + (err.response?.data?.error || err.message));
    } finally {
      setRefreshingCache(false);
    }
  };

  // Fonction pour rediriger vers le bon dashboard selon la plateforme
  const handleViewDashboard = (platform) => {
    // Convertir le nom de plateforme en clé du format utilisé dans platformAccountDetails
    let platformKey;
    switch (platform) {
      case 'Google Ads':
        platformKey = 'GOOGLE_ADS';
        break;
      case 'Microsoft Ads':
        platformKey = 'MICROSOFT_ADS';
        break;
      case 'Meta Ads':
        platformKey = 'FACEBOOK_ADS';
        break;
      case 'Pinterest Ads':
        platformKey = 'PINTEREST_ADS';
        break;
      case 'TikTok Ads':
        platformKey = 'TIKTOK_ADS';
        break;
      case 'ChatGPT Ads':
        platformKey = 'OPENAI_ADS';
        break;
      case 'Awin':
        platformKey = 'AWIN';
        break;
      default:
        platformKey = platform;
    }

    // Vérifier combien de comptes sont attachés à cette plateforme
    const accounts = platformAccountDetails[platformKey] || [];
    
    console.log('🔍 [PLATFORM VIEW] Plateforme cliquée:', platform, '(clé:', platformKey, ') avec', accounts.length, 'comptes');
    console.log('🔍 [PLATFORM VIEW] Clés disponibles dans platformAccountDetails:', Object.keys(platformAccountDetails));
    
    // Si un seul compte, le sélectionner automatiquement
    if (accounts.length === 1) {
      const singleAccount = accounts[0];
      console.log('🎯 [PLATFORM VIEW] Un seul compte trouvé, sélection automatique:', singleAccount);
      
      handleViewAccountDashboard({
        accountId: singleAccount.sourceId || singleAccount.accountId || singleAccount.id,
        accountType: platformKey
      });
      return;
    }
    
    // Sinon, naviguer vers le dashboard global sans sélection
    console.log('🎯 [PLATFORM VIEW] Plusieurs comptes, navigation vers dashboard global');

    // Définir le type de dashboard selon la plateforme
    let dashboardType;
    switch (platform) {
      case 'Google Ads':
        dashboardType = 'google';
        break;
      case 'Microsoft Ads':
        dashboardType = 'microsoft';
        break;
      case 'Meta Ads':
        dashboardType = 'facebook-ads';
        break;
      case 'Pinterest Ads':
        dashboardType = 'pinterest-ads';
        break;
      case 'TikTok Ads':
        dashboardType = 'tiktok-ads';
        break;
      case 'ChatGPT Ads':
        dashboardType = 'chatgpt-ads';
        break;
      case 'Awin':
        dashboardType = 'awin';
        break;
      default:
        dashboardType = 'google'; // Par défaut
    }

    // Naviguer vers le dashboard avec le bon type (sans account)
    if (navigateToDashboardWithAccount) {
      navigateToDashboardWithAccount(dashboardType, null);
    } else {
      setCurrentDashboardType(dashboardType);
      setCurrentView('dashboard');
    }
  };

  // Fonction pour rediriger vers un compte spécifique avec sélection automatique
  const handleViewAccountDashboard = (account) => {
    /*
     * ⚠️ **L'écran de choix d'abord, s'il y a un choix.**
     *
     * `ouvrirChoixDashboard` ne s'affiche que si le compte a plus d'une destination — sinon il
     * appelle lui-même le dashboard de la plateforme, et rien ne change. C'est ce qui donne une
     * porte aux dashboards sur mesure posés sur une régie : sans elle, un compte Google Ads
     * ouvrait droit son dashboard, et ce qu'on avait hébergé dessus n'existait nulle part.
     */
    if (ouvrirChoixDashboard && account.accountType) {
      ouvrirChoixDashboard(account.accountType, account.accountId, account.accountName || null, 'home');
      return;
    }

    // Stocker les informations de navigation dans localStorage
    const navigationData = {
      accountId: account.accountId,
      accountType: account.accountType,
      period: selectedPeriod,
      customStartDate: customStartDate,
      customEndDate: customEndDate
    };
    localStorage.setItem('kokpeet_selectedAccount', JSON.stringify(navigationData));

    // Déterminer l'onglet cible selon le type de compte
    let targetTab;
    switch (account.accountType) {
      case 'MICROSOFT_ADS':
        targetTab = 'microsoft';
        break;
      case 'FACEBOOK_ADS':
        targetTab = 'facebook-ads';
        break;
      case 'PINTEREST_ADS':
        targetTab = 'pinterest-ads';
        break;
      case 'TIKTOK_ADS':
        targetTab = 'tiktok-ads';
        break;
      case 'OPENAI_ADS':
        targetTab = 'chatgpt-ads';
        break;
      // Sans ce cas, le `default` ci-dessous ouvrirait le dashboard Google Ads.
      case 'AWIN':
        targetTab = 'awin';
        break;
      case 'GOOGLE_ADS':
      default:
        targetTab = 'google';
        break;
    }
    localStorage.setItem('kokpeet_targetTab', targetTab);

    // Déclencher un événement personnalisé pour la navigation
    window.dispatchEvent(new CustomEvent('forceNavigation', {
      detail: { targetTab, navigationData }
    }));

    // Naviguer vers le dashboard avec le bon type et l'account dans l'URL
    if (navigateToDashboardWithAccount) {
      navigateToDashboardWithAccount(targetTab, account.accountId);
    } else {
      setCurrentDashboardType(targetTab);
      setCurrentView('dashboard');
    }
  };

  // Charger les données de synthèse par plateforme
  const loadPlatformData = async () => {
    setLoadingTableData(true);
    setError('');
    
    try {
      // Récupérer les données de performance depuis l'endpoint existant
      const params = {
        period: selectedPeriod
      };
      if (selectedPeriod === 'CUSTOM') {
        params.customStartDate = customStartDate;
        params.customEndDate = customEndDate;
      }

      // Ajouter le filtre client si un client est sélectionné
      if (selectedClientId) {
        params.clientId = selectedClientId;
        console.log('🎯 [CLIENT FILTER] Filtrage par client:', selectedClientId);
      } else {
        console.log('🎯 [CLIENT FILTER] Aucun client sélectionné, affichage de tous les données');
      }

      /**
       * La base de comparaison choisie dans le titre — envoyée **seulement** si elle n'est
       * pas celle par défaut.
       *
       * La présence du paramètre fait contourner le cache des sept jours (`synthese.js`
       * l. 269 et 1273), que `runRecommendationsTasks` calcule à 3 h pour tout le parc.
       * L'envoyer même pour « période précédente » revenait à recalculer à chaque affichage
       * ce qui était déjà prêt : mesuré à 0,02 s servi par le cache contre 6,4 s recalculé.
       *
       * « Année précédente » n'est pas en cache : le cron n'en calcule qu'une seule
       * comparaison, la période immédiatement précédente. Le contournement y est donc exact,
       * et non un effet de bord.
       */
      if (baseComparaison !== 'previous_period') {
        params.comparisonType = baseComparaison;
      }

      // Une comparaison personnalisée réglée ailleurs garde la main : ses dates explicites
      // priment sur le type, côté serveur comme ici.
      if (compareEnabled && comparisonStartDate && comparisonEndDate) {
        params.comparisonStartDate = comparisonStartDate;
        params.comparisonEndDate = comparisonEndDate;
      }

      console.log('🎯 [API CALL] Paramètres envoyés:', params);
      
      const performanceResponse = await axios.get(`${API_URL}/api/synthese/performance`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      console.log('🎯 [API RESPONSE] Données reçues:', performanceResponse.data);
      console.log('🎯 [API RESPONSE] Sites dans la réponse:', performanceResponse.data.data?.map(site => ({ name: site.siteName, id: site.siteId })));
      
      if (performanceResponse.data.success) {
        // Agréger les données par plateforme depuis les données de sites
        const platformAggregation = {};
        const platformAccountsDetails = {};
        const siteData = performanceResponse.data.data || [];

        // Filtrer par site si sélectionné (vérifier plusieurs formats d'ID)
        const filteredSites = (selectedSiteId && selectedSiteId !== 'all')
          ? siteData.filter(site => {
              const siteId = site.siteId || site._id || site.id;
              return siteId === selectedSiteId;
            })
          : siteData;

        filteredSites.forEach(site => {
          if (site.accounts && site.accounts.length > 0) {
            site.accounts.forEach(account => {
              const platformKey = account.accountType;
              
              if (!platformAggregation[platformKey]) {
                platformAggregation[platformKey] = {
                  platform: platformKey,
                  accountCount: 0,
                  clicks: 0,
                  cost: 0,
                  conversions: 0,
                  conversionValue: 0,
                  previousPeriod: {
                    clicks: 0,
                    cost: 0,
                    conversions: 0,
                    conversionValue: 0
                  },
                  hasExpiredTokens: false,
                  expiredTokenMessage: null,
                  comptesNonMesures: 0,
                  messageNonMesure: null
                };
                platformAccountsDetails[platformKey] = [];
              }

              // Vérifier si le compte a un token expiré
              if (account.tokenExpired) {
                platformAggregation[platformKey].hasExpiredTokens = true;
                /*
                 * ⚠️ Le message du serveur est conservé **tel quel** : c'est lui qui nomme le
                 * refus, et c'est de lui que le badge tire son mot (`libelleRefusConnecteur`).
                 * Le repli ne sert que si la régie n'a rien dit — un message écrit ici en dur
                 * ferait annoncer « Token expiré » sur une clé Awin, qui n'expire jamais.
                 */
                platformAggregation[platformKey].expiredTokenMessage = account.tokenExpiredMessage || 'Token expiré - reconnexion requise';
                console.log(`⚠️ [TOKEN EXPIRED] ${account.accountName} (${platformKey}): ${account.tokenExpiredMessage}`);
              }

              /**
               * ⚠️ **Un compte que la régie n'a pas pu mesurer pèse zéro dans ce total.**
               *
               * Le cache rend des zéros pour une mesure qui a échoué — un `null` fabriquerait un
               * `NaN` qui contaminerait la plateforme entière —, et c'est `mesureIndisponible` qui
               * porte le sens. Sans cette lecture, la ligne Meta d'un compte dont l'appel a été
               * refusé pour quota affiche « 0 € » : une régie à l'arrêt, et une baisse de 100 %
               * dans la variation, pendant les vingt-quatre heures de vie du cache.
               */
              if (account.mesureIndisponible) {
                platformAggregation[platformKey].comptesNonMesures += 1;
                if (!platformAggregation[platformKey].messageNonMesure) {
                  platformAggregation[platformKey].messageNonMesure = account.mesureIndisponibleMessage
                    || 'La régie n\'a pas répondu lors du dernier calcul.';
                }
                console.log(`⚠️ [MESURE INDISPONIBLE] ${account.accountName} (${platformKey}): ${account.mesureIndisponibleMessage}`);
              }
              
              // Calculer les métriques et taux de croissance pour le compte
              const currentCpa = (account.conversions || 0) > 0 ? (account.cost || 0) / (account.conversions || 0) : 0;
              const currentCos = (account.conversionsValue || 0) > 0 ? ((account.cost || 0) / (account.conversionsValue || 0)) * 100 : 0;
              
              // Calculer le COS de la période précédente
              const previousCos = account.previousPeriod && (account.previousPeriod.conversionsValue || 0) > 0 
                ? ((account.previousPeriod.cost || 0) / (account.previousPeriod.conversionsValue || 0)) * 100 
                : 0;

              // Fonction pour calculer le taux de croissance
              const calculateGrowthRate = (current, previous) => {
                // Si les deux valeurs sont 0 ou nulles, pas de changement
                if ((!current || current === 0) && (!previous || previous === 0)) return null;
                
                // Pas de base : la variation n'est pas calculable. L'écran le dit « nouveau ».
                if ((!previous || previous === 0) && current > 0) return DEPUIS_ZERO;
                
                // Si la valeur actuelle est 0 mais qu'il y avait une valeur précédente, c'est une baisse de 100%
                if ((!current || current === 0) && previous > 0) return -100;
                
                // Calcul normal du pourcentage
                return ((current - previous) / previous) * 100;
              };

              let accountGrowthRates = {
                clicks: null,
                cost: null,
                conversions: null,
                conversionValue: null,
                cpa: null,
                cos: calculateGrowthRate(currentCos, previousCos) // Calculer le taux de croissance du COS
              };

              if (account.previousPeriod && account.growthRates) {
                accountGrowthRates = {
                  ...account.growthRates,
                  cos: calculateGrowthRate(currentCos, previousCos) // Remplacer par notre calcul du COS
                };
              }

              // Ajouter les détails du compte
              platformAccountsDetails[platformKey].push({
                ...account,
                siteName: site.siteName,
                siteId: site.siteId,
                cpa: currentCpa,
                cos: currentCos,
                growthRates: accountGrowthRates
              });
              
              // Agréger les totaux de la plateforme
              platformAggregation[platformKey].accountCount += 1;
              platformAggregation[platformKey].clicks += account.clicks || 0;
              platformAggregation[platformKey].cost += account.cost || 0;
              platformAggregation[platformKey].conversions += account.conversions || 0;
              platformAggregation[platformKey].conversionValue += account.conversionsValue || 0;

              // Ajouter les données de la période précédente pour l'agrégation
              if (account.previousPeriod) {
                const prevClicks = Number(account.previousPeriod.clicks) || 0;
                const prevCost = Number(account.previousPeriod.cost) || 0;
                const prevConversions = Number(account.previousPeriod.conversions) || 0;
                const prevConversionValue = Number(account.previousPeriod.conversionsValue) || 0;

                platformAggregation[platformKey].previousPeriod.clicks += prevClicks;
                platformAggregation[platformKey].previousPeriod.cost += prevCost;
                platformAggregation[platformKey].previousPeriod.conversions += prevConversions;
                platformAggregation[platformKey].previousPeriod.conversionValue += prevConversionValue;
                
                // Debug pour voir les valeurs de chaque compte
                if (platformKey === 'GOOGLE_ADS') {
                  console.log(`🔍 [ACCOUNT DEBUG] ${account.accountName}:`, {
                    current: { conversionValue: account.conversionsValue },
                    previous: { 
                      conversionValue: account.previousPeriod.conversionValue,
                      conversionsValue: account.previousPeriod.conversionsValue,
                      cost: account.previousPeriod.cost,
                      conversions: account.previousPeriod.conversions
                    },
                    converted: { prevConversionValue },
                    hasGrowthRates: !!account.growthRates,
                    growthRates: account.growthRates
                  });
                }
              } else {
                console.log(`⚠️ [MISSING PREVIOUS] ${account.accountName} (${platformKey}) n'a pas de previousPeriod`);
              }
            });
          }
        });
        
        // Trier les comptes par budget décroissant dans chaque plateforme
        Object.keys(platformAccountsDetails).forEach(platformKey => {
          platformAccountsDetails[platformKey].sort((a, b) => (b.cost || 0) - (a.cost || 0));
        });
        
        // Calculer le CPA, COS et les taux de croissance à partir des totaux agrégés
        const platformData = Object.values(platformAggregation).map(platform => {
          const currentCpa = platform.conversions > 0 ? platform.cost / platform.conversions : 0;
          const currentCos = platform.conversionValue > 0 ? (platform.cost / platform.conversionValue) * 100 : 0;
          
          const previousCpa = platform.previousPeriod.conversions > 0 ? platform.previousPeriod.cost / platform.previousPeriod.conversions : 0;
          const previousCos = platform.previousPeriod.conversionValue > 0 ? (platform.previousPeriod.cost / platform.previousPeriod.conversionValue) * 100 : 0;

          // Debug pour comprendre le problème
          console.log(`🔍 [PLATFORM DEBUG] ${platform.platform}:`, {
            accountCount: platform.accountCount,
            current: {
              cost: platform.cost,
              conversions: platform.conversions,
              conversionValue: platform.conversionValue
            },
            previous: {
              cost: platform.previousPeriod.cost,
              conversions: platform.previousPeriod.conversions,
              conversionValue: platform.previousPeriod.conversionValue
            },
            conversionValueGrowthCalc: `(${platform.conversionValue} - ${platform.previousPeriod.conversionValue}) / ${platform.previousPeriod.conversionValue} * 100`
          });

          // Calculer les taux de croissance à partir des totaux agrégés (correct)
          const calculateGrowth = (current, previous) => {
            if ((!current || current === 0) && (!previous || previous === 0)) return null;
            if ((!previous || previous === 0) && current > 0) return DEPUIS_ZERO;
            if ((!current || current === 0) && previous > 0) return -100;
            return ((current - previous) / previous) * 100;
          };

          const growthRates = {
            clicks: calculateGrowth(platform.clicks, platform.previousPeriod.clicks),
            cost: calculateGrowth(platform.cost, platform.previousPeriod.cost),
            conversions: calculateGrowth(platform.conversions, platform.previousPeriod.conversions),
            conversionValue: calculateGrowth(platform.conversionValue, platform.previousPeriod.conversionValue),
            cpa: calculateGrowth(currentCpa, previousCpa),
            cos: calculateGrowth(currentCos, previousCos)
          };

          console.log(`🔍 [PLATFORM GROWTH] ${platform.platform}:`, growthRates);

          return {
            ...platform,
            cpa: currentCpa,
            cos: currentCos,
            growthRates
          };
        });
        
        // Trier par budget (coût) décroissant par défaut
        platformData.sort((a, b) => b.cost - a.cost);
        
        console.log('🎯 [PLATFORM DATA] Données finales du tableau:', platformData);
        
        setPlatformData(platformData);
        setPlatformAccountDetails(platformAccountsDetails);
      } else {
        throw new Error(performanceResponse.data.error || 'Erreur lors du chargement des données');
      }
    } catch (err) {
      console.error('Erreur page d\'accueil:', err);
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setLoadingTableData(false);
    }
  };

  // Fonction pour charger les données Analytics (Matomo, GA4, etc.)
  const loadAnalyticsData = async () => {
    setLoadingAnalyticsData(true);

    try {
      const params = {
        period: selectedPeriod
      };
      if (selectedPeriod === 'CUSTOM') {
        params.customStartDate = customStartDate;
        params.customEndDate = customEndDate;
      }

      // Ajouter le filtre client si un client est sélectionné (et non vide)
      if (selectedClientId && String(selectedClientId).trim() !== '' && selectedClientId !== 'all') {
        params.clientId = selectedClientId;
      }

      // Ajouter le filtre site si un site est sélectionné (et non vide, et pas "all")
      if (selectedSiteId && String(selectedSiteId).trim() !== '' && selectedSiteId !== 'all') {
        params.siteId = selectedSiteId;
      }

      // Même base de comparaison que le tableau des plateformes : les trois tableaux de la
      // page se lisent ensemble, une référence différente de l'un à l'autre les opposerait.
      // Omise quand elle est celle par défaut, pour ne pas contourner le cache des sept jours.
      if (baseComparaison !== 'previous_period') {
        params.comparisonType = baseComparaison;
      }

      if (compareEnabled && comparisonStartDate && comparisonEndDate) {
        params.comparisonStartDate = comparisonStartDate;
        params.comparisonEndDate = comparisonEndDate;
      }

      console.log('📊 [ANALYTICS] Chargement données Analytics:', params, '| selectedClientId:', selectedClientId, '| selectedSiteId:', selectedSiteId);

      const response = await axios.get(`${API_URL}/api/synthese/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      console.log('📊 [ANALYTICS] Réponse:', response.data);

      if (response.data.success) {
        setAnalyticsData(response.data.data || []);
      } else {
        console.warn('📊 [ANALYTICS] Pas de données:', response.data.error);
        setAnalyticsData([]);
      }
    } catch (err) {
      console.error('📊 [ANALYTICS] Erreur chargement:', err);
      setAnalyticsData([]);
    } finally {
      setLoadingAnalyticsData(false);
    }
  };




  // Charger TOUTES les données de la HomePage en UN seul useEffect consolidé
  useEffect(() => {
    // Ne pas charger si période CUSTOM sans dates définies
    if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
      console.log('📅 [HomePage] Période CUSTOM sélectionnée, en attente des dates...');
      return;
    }

    // Charger les données en parallèle
    loadPlatformData();
    loadAnalyticsData(); // Données Analytics (Matomo, GA4, etc.)
  }, [token, selectedClientId, selectedSiteId, selectedPeriod, customStartDate, customEndDate, baseComparaison, compareEnabled, comparisonType, comparisonStartDate, comparisonEndDate]);

  // Gestionnaire pour les changements de période depuis la sidebar
  useEffect(() => {
    const handlePeriodChange = (event) => {
      const { period } = event.detail;
      console.log('📅 HomePage - Changement de période depuis sidebar:', period);
      setSelectedPeriod(period);
    };

    window.addEventListener('periodChange', handlePeriodChange);
    return () => {
      window.removeEventListener('periodChange', handlePeriodChange);
    };
  }, []);

  // Gestionnaire pour le rafraîchissement des données (quand des comptes sont ajoutés/supprimés)
  useEffect(() => {
    const handleDataChange = () => {
      console.log('🔄 HomePage - Rafraîchissement des données suite à un changement');
      loadPlatformData();
      loadAnalyticsData();
    };

    window.addEventListener('homepageDataChange', handleDataChange);
    return () => {
      window.removeEventListener('homepageDataChange', handleDataChange);
    };
  }, [token, selectedClientId, selectedSiteId, selectedPeriod, customStartDate, customEndDate]);

  // Synchroniser showCustomDates avec selectedPeriod
  useEffect(() => {
    setShowCustomDates(selectedPeriod === 'CUSTOM');
  }, [selectedPeriod]);

  // Formater les nombres
  const formatNumber = (value, decimals = 0) => {
    if (value === null || value === undefined) return '-';
    if (typeof value !== 'number') return value;
    return value.toLocaleString('fr-FR', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  };

  // Formater les devises
  const formatCurrency = (value, currency = 'EUR') => {
    if (value === null || value === undefined) return '-';
    if (typeof value !== 'number') return value;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Formater le CPA avec une seule décimale
  const formatCPA = (value, currency = 'EUR') => {
    if (value === null || value === undefined) return '-';
    if (typeof value !== 'number') return value;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  };

  // Formater le taux de croissance.
  // Même présentation que les tableaux des dashboards : flèche selon le signe,
  // vert / rouge selon que la variation est un progrès, gris et « → » à zéro.
  const formatGrowthRate = (value, metricType = 'default') => {
    if (value === null || value === undefined) return null;
    if (typeof value !== 'number') return null;

    // Pour le budget, le CPA et le COS, une hausse est une dégradation.
    const inverted = metricType === 'cost' || metricType === 'cpa' || metricType === 'cos';

    /*
     * Bornes d'affichage : au-delà, le pourcentage exact n'apporte plus rien.
     *
     * ⚠️ Le marqueur « partir de zéro » passe **avant** le plafond : l'écraser à 999 lui ferait
     * reprendre l'apparence du chiffre inventé qu'on vient de supprimer, et le rendrait de
     * nouveau indistinguable d'une croissance réelle de plus de 999 %.
     */
    if (value === DEPUIS_ZERO) return renderGrowthRate(DEPUIS_ZERO, inverted);
    const clamped = value >= 999 ? 999 : value <= -99.9 ? -100 : value;

    return renderGrowthRate(clamped, inverted);
  };


  // Obtenir l'icône de la plateforme
  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'google_ads':
      case 'google':
        return <img src={googleAdsLogo} alt="Google Ads" style={{width: '24px', height: '24px'}} />;
      case 'microsoft_ads':
      case 'microsoft':
        return <img src={microsoftAdsLogo} alt="Microsoft Ads" style={{width: '24px', height: '24px'}} />;
      case 'facebook_ads':
      case 'facebook':
        return <img src={metaAdsLogo} alt="Meta Ads" style={{width: '24px', height: '24px', objectFit: 'contain'}} />;
      case 'pinterest_ads':
      case 'pinterest':
        return <img src={pinterestAdsLogo} alt="Pinterest Ads" style={{width: '24px', height: '24px', objectFit: 'contain'}} />;
      case 'tiktok_ads':
      case 'tiktok':
        return <img src={tiktokAdsLogo} alt="TikTok Ads" style={{width: '24px', height: '24px', objectFit: 'contain'}} />;
      case 'openai_ads':
      case 'chatgpt':
        return <img src={chatgptAdsLogo} alt="ChatGPT Ads" style={{width: '24px', height: '24px', objectFit: 'contain'}} />;
      case 'awin':
        return <img src={awinLogo} alt="Awin" style={{width: '24px', height: '24px', objectFit: 'contain'}} />;
      default:
        return '📊';
    }
  };

  // Obtenir le nom de la plateforme
  const getPlatformName = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'google_ads':
      case 'google':
        return 'Google Ads';
      case 'microsoft_ads':
      case 'microsoft':
        return 'Microsoft Ads';
      case 'facebook_ads':
      case 'facebook':
        return 'Meta Ads';
      case 'pinterest_ads':
      case 'pinterest':
        return 'Pinterest Ads';
      case 'tiktok_ads':
      case 'tiktok':
        return 'TikTok Ads';
      case 'openai_ads':
      case 'chatgpt':
        return 'ChatGPT Ads';
      case 'awin':
        return 'Awin';
      default:
        return platform;
    }
  };

  // Gérer l'expansion des lignes
  const togglePlatformExpansion = (platform) => {
    const newExpanded = new Set(expandedPlatforms);
    if (newExpanded.has(platform)) {
      newExpanded.delete(platform);
    } else {
      newExpanded.add(platform);
    }
    setExpandedPlatforms(newExpanded);
  };

  // === Fonctions pour le tableau Analytics ===

  // Obtenir l'icône de la source Analytics
  const getAnalyticsIcon = (source) => {
    switch (source?.toUpperCase()) {
      case 'MATOMO':
        return (
          <img
            src={matomoLogo}
            alt="Matomo"
            style={{ width: '24px', height: '24px', borderRadius: '4px' }}
          />
        );
      case 'GA4':
      case 'GOOGLE_ANALYTICS':
        return (
          <img
            src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png"
            alt="Google Analytics"
            style={{ width: '24px', height: '24px', borderRadius: '4px' }}
          />
        );
      case 'PIANO':
        return (
          <img
            src={pianoLogo}
            alt="Piano Analytics"
            style={{ width: '24px', height: '24px', borderRadius: '4px' }}
          />
        );
      default:
        return '📊';
    }
  };

  // Obtenir le nom de la source Analytics
  const getAnalyticsSourceName = (source) => {
    switch (source?.toUpperCase()) {
      case 'MATOMO':
        return 'Matomo';
      case 'GA4':
      case 'GOOGLE_ANALYTICS':
        return 'Google Analytics';
      case 'PIANO':
        return 'Piano Analytics';
      default:
        return source;
    }
  };


  // Gérer l'expansion des sources Analytics
  const toggleAnalyticsSourceExpansion = (source) => {
    const newExpanded = new Set(expandedAnalyticsSources);
    if (newExpanded.has(source)) {
      newExpanded.delete(source);
    } else {
      newExpanded.add(source);
    }
    setExpandedAnalyticsSources(newExpanded);
  };

  /**
   * « Voir » ouvre le choix du dashboard, pas un dashboard.
   *
   * Cette ligne mène à deux écrans, et rien ici ne permet de deviner lequel on veut : le
   * dashboard de la solution de mesure, ou le consolidé qui la croise avec les régies. Le
   * tableau Consolidé posait la question en doublant les mêmes chiffres dans un second
   * tableau — mêmes sources, mêmes colonnes, même appel. La question se pose désormais une
   * fois, au moment où elle se pose vraiment : quand on a cliqué.
   *
   * Le type transmis est celui du `LibraryItem` (`GOOGLE_ANALYTICS`, `PIANO_ANALYTICS`,
   * `MATOMO`), et non la clé du tableau de synthèse (« GA4 », « PIANO ») : c'est lui que le
   * dashboard consolidé attend pour se cadrer sur une solution.
   */
  const TYPE_PAR_SOURCE = {
    MATOMO: 'MATOMO',
    GA4: 'GOOGLE_ANALYTICS',
    GOOGLE_ANALYTICS: 'GOOGLE_ANALYTICS',
    PIANO: 'PIANO_ANALYTICS',
    PIANO_ANALYTICS: 'PIANO_ANALYTICS',
    'PIANO ANALYTICS': 'PIANO_ANALYTICS'
  };

  const handleViewAnalyticsDashboard = (source, siteId = null) => {
    const type = TYPE_PAR_SOURCE[source?.toUpperCase()];
    // Une source inconnue ne mène nulle part, comme avant : mieux vaut un bouton sans effet
    // qu'un écran de choix entre deux destinations qu'on ne sait pas construire.
    if (!type) return;
    ouvrirChoixDashboard?.(type, siteId);
  };

  // Calculer les totaux pour toutes les plateformes
  const totals = useMemo(() => {
    // Fonction pour calculer le taux de croissance (définie ici pour être disponible dans tous les cas)
    const calculateGrowthRate = (current, previous) => {
      if ((!current || current === 0) && (!previous || previous === 0)) return null;
      if ((!previous || previous === 0) && current > 0) return DEPUIS_ZERO;
      if ((!current || current === 0) && previous > 0) return -100;
      return ((current - previous) / previous) * 100;
    };

    if (!platformData || platformData.length === 0) {
      return {
        totalAccounts: 0,
        totalClicks: 0,
        totalCost: 0,
        totalPreviousCost: 0,
        totalConversions: 0,
        totalConversionValue: 0,
        avgCpa: 0,
        avgCos: 0,
        growthRates: {
          clicks: null,
          cost: null,
          conversions: null,
          conversionValue: null,
          cpa: null,
          cos: null
        },
        calculateGrowthRate
      };
    }

    const totalAccounts = platformData.reduce((sum, platform) => sum + (platform.accountCount || 0), 0);
    const totalClicks = platformData.reduce((sum, platform) => sum + (platform.clicks || 0), 0);
    const totalCost = platformData.reduce((sum, platform) => sum + (platform.cost || 0), 0);
    const totalConversions = platformData.reduce((sum, platform) => sum + (platform.conversions || 0), 0);
    const totalConversionValue = platformData.reduce((sum, platform) => sum + (platform.conversionValue || 0), 0);

    // Calculer les totaux de la période précédente
    const totalPreviousClicks = platformData.reduce((sum, platform) => sum + (platform.previousPeriod?.clicks || 0), 0);
    const totalPreviousCost = platformData.reduce((sum, platform) => sum + (platform.previousPeriod?.cost || 0), 0);
    const totalPreviousConversions = platformData.reduce((sum, platform) => sum + (platform.previousPeriod?.conversions || 0), 0);
    const totalPreviousConversionValue = platformData.reduce((sum, platform) => sum + (platform.previousPeriod?.conversionValue || 0), 0);
    
    // Calculer CPA et COS moyens (période actuelle)
    const avgCpa = totalConversions > 0 ? totalCost / totalConversions : 0;
    const avgCos = totalConversionValue > 0 ? (totalCost / totalConversionValue) * 100 : 0;
    
    // Calculer CPA et COS moyens (période précédente)
    const previousAvgCpa = totalPreviousConversions > 0 ? totalPreviousCost / totalPreviousConversions : 0;
    const previousAvgCos = totalPreviousConversionValue > 0 ? (totalPreviousCost / totalPreviousConversionValue) * 100 : 0;

    // Calculer les taux de croissance des totaux
    const growthRates = {
      clicks: calculateGrowthRate(totalClicks, totalPreviousClicks),
      cost: calculateGrowthRate(totalCost, totalPreviousCost),
      conversions: calculateGrowthRate(totalConversions, totalPreviousConversions),
      conversionValue: calculateGrowthRate(totalConversionValue, totalPreviousConversionValue),
      cpa: calculateGrowthRate(avgCpa, previousAvgCpa),
      cos: calculateGrowthRate(avgCos, previousAvgCos)
    };

    return {
      totalAccounts,
      totalClicks,
      totalCost,
      totalPreviousCost,
      totalConversions,
      totalConversionValue,
      avgCpa,
      avgCos,
      growthRates,
      calculateGrowthRate
    };
  }, [platformData]);

  /**
   * Ce que « Conv. » et « Valeur Conv. » valent, solution de mesure par solution de mesure.
   *
   * ## La décision se prend par propriété, pas par source
   *
   * Chaque propriété GA4 porte un **événement clé choisi** dans ses réglages — `add_to_cart`,
   * `view_item_list`, `generate_lead` — et le serveur rend son dénombrement sous
   * `conversions`, sa valeur sous `conversionsValue`. Les lire tels quels pour tout le monde
   * donnait une colonne qui ne voulait rien dire :
   *
   * | Propriété | « Conv. » lu | « Valeur Conv. » lue | Transactions | CA réel |
   * |---|---|---|---|---|
   * | Happy Garden (`view_item_list`) | 16 515 | 0 € | 457 | 100 647 € |
   * | Pebeo (`add_to_cart`) | 6 065 | **79 881 €** | 77 | 3 594 € |
   * | BizzBee (événements clés) | 32 160 | 131 078 € | 2 397 | 131 078 € |
   *
   * Pebeo annonçait ainsi 79 881 € de « valeur de conversion » : c'est la valeur des articles
   * **mis au panier**, pas une vente. Et la ligne agrégée additionnait cette valeur de panier
   * avec le chiffre d'affaires de BizzBee — 210 959 € pour 235 319 € réels.
   *
   * Les prendre toutes sur les transactions n'allait pas non plus : une propriété de
   * génération de leads n'a ni transaction ni chiffre d'affaires, et ses demandes de devis
   * disparaissaient.
   *
   * **D'où une décision par propriété** : celle qui vend porte ses ventes et son chiffre
   * d'affaires, les autres portent leur objectif et sa valeur. C'est la convention du tableau
   * des régies juste en dessous, où chaque compte compte les conversions qu'il a définies.
   *
   * ## Pourquoi la mesure décide, et non le réglage
   *
   * Le réglage « Événement à compter » du dashboard GA4 ne sait pas dire « transactions » : il
   * propose les événements clés ou un événement précis. Et côté Matomo, le sélecteur
   * d'objectif n'a pas de serveur derrière lui. La propriété qui vend se reconnaît donc à ce
   * que la solution de mesure en rapporte.
   *
   * **La période courante et la période de comparaison sont examinées ensemble**, pour que les
   * deux moitiés d'une variation parlent du même dénombrement. Sans cela, une boutique sans
   * vente cette semaine basculerait sur son objectif et comparerait des ajouts au panier à des
   * transactions.
   *
   * ⚠️ Une boutique sans aucune vente sur les deux périodes affiche son objectif. Le survol le
   * dit — c'est à cela que sert la mention.
   *
   * ## Les trois solutions ne mesurent pas la même chose, et le survol le dit
   *
   * | Source | Quand elle vend | Sinon |
   * |---|---|---|
   * | GA4 | `transactions` · `totalRevenue` | l'événement clé réglé, et sa valeur |
   * | Matomo | commandes e-commerce · revenu | l'objectif retenu, ou tous |
   * | Piano | toujours : `m_transactions` · `m_v_sales` | — |
   *
   * Deux particularités que la mention nomme, faute de pouvoir les corriger ici :
   *
   * - **Matomo compte désormais ses conversions, pas ses visites converties.** Il rendait
   *   `nb_visits_converted` — une visite atteignant trois objectifs y comptait pour 1 —, et sa
   *   colonne Conv. ne disait donc pas la même chose que celle des régies. Elle vaut
   *   maintenant `nb_conversions` de l'objectif réglé sur le dashboard Matomo, ou de tous.
   * - **Piano ne mesure que l'e-commerce ici.** Ses champs `conversions` et `conversionsValue`
   *   portent déjà `m_transactions` et `m_v_sales` — le service les y range sous ces noms
   *   (`pianoAnalytics.js`, `getConversions`). Une ligne Piano est donc toujours en
   *   transactions, et ne bascule jamais : il n'y a pas d'autre dénombrement à proposer.
   */
  const estGA4 = (source) => source?.source === 'GOOGLE_ANALYTICS';

  /**
   * Cette propriété vend-elle ?
   *
   * Le chiffre d'affaires est examiné autant que les ventes : une commande peut être
   * enregistrée sans identifiant valide, auquel cas le revenu existe sans la transaction.
   *
   * Piano ne remplit aucun des deux : ses propriétés répondent toujours non, sans qu'il faille
   * l'écrire en dur.
   */
  const proprieteVend = (propriete) => (
    (propriete?.metrics?.transactions || 0) > 0 ||
    (propriete?.metrics?.totalRevenue || 0) > 0 ||
    (propriete?.previousPeriod?.transactions || 0) > 0 ||
    (propriete?.previousPeriod?.totalRevenue || 0) > 0
  );

  /**
   * La mesure e-commerce a-t-elle eu lieu ?
   *
   * ⚠️ **`=== null`, strictement.** Le serveur distingue désormais trois états : un nombre,
   * `null` quand l'appel a échoué, et l'absence du champ pour une source qui n'a pas de
   * mesure e-commerce du tout — c'est le cas de Piano, dont les propriétés ne portent jamais
   * `transactions`. Un `== null` large les confondrait et annoncerait « non mesuré » sur
   * chaque ligne Piano.
   *
   * Pourquoi cela compte : sans ce signal, un `Goals.get` abandonné au bout de 30 secondes
   * faisait passer une boutique pour un site qui ne vend pas. `proprieteVend` répondait non,
   * la colonne Conv. basculait sur l'objectif, et le survol affirmait « Objectif retenu sur ce
   * site » — exactement ce qu'on vient y chercher quand on doute du chiffre.
   */
  const ecommerceNonMesure = (propriete) => propriete?.metrics?.transactions === null;

  /** Comment nommer l'événement clé d'une propriété GA4 — voir `evenementsCles` côté serveur. */
  const nomEvenementCle = (evenements) => (
    !evenements || evenements.length === 0
      ? 'les événements clés marqués dans GA4'
      : evenements.join(', ')
  );

  /**
   * Ce qu'une propriété porte dans « Conv. » : le champ à lire, et la phrase qui le dit.
   *
   * Le champ et la phrase sortent **ensemble**, d'un seul endroit. Les séparer laisserait la
   * mention décrire autre chose que le nombre affiché — un mensonge d'autant plus coûteux
   * qu'on l'a justement mise là pour qu'on s'y fie.
   */
  const origineConversions = (source, propriete) => {
    if (proprieteVend(propriete)) {
      return {
        champ: 'transactions',
        phrase: estGA4(source)
          ? 'Transactions e-commerce de GA4'
          : 'Commandes e-commerce de Matomo'
      };
    }

    switch (source?.source) {
      case 'GOOGLE_ANALYTICS':
        return { champ: 'conversions', phrase: `Événement clé : ${nomEvenementCle(propriete?.evenementsCles)}` };
      case 'MATOMO':
        return {
          champ: 'conversions',
          phrase: (propriete?.evenementsCles?.length
            ? 'Objectif retenu sur ce site'
            : 'Conversions, tous objectifs confondus')
            // La bascule n'est pas un constat si la mesure e-commerce a échoué : le dire, sans
            // quoi la mention affirmerait que ce site ne vend pas.
            + (ecommerceNonMesure(propriete)
              ? ' — les commandes e-commerce n\'ont pas pu être mesurées'
              : '')
        };
      // Piano range ses transactions sous `conversions` : le champ est le même, la phrase
      // doit dire ce qu'il contient réellement.
      case 'PIANO':
        return { champ: 'conversions', phrase: 'Transactions e-commerce de Piano' };
      default:
        return { champ: 'conversions', phrase: null };
    }
  };

  /** Idem pour « Valeur Conv. ». */
  const origineValeur = (source, propriete) => {
    if (proprieteVend(propriete)) {
      return {
        champ: 'totalRevenue',
        phrase: estGA4(source)
          ? 'Chiffre d\'affaires e-commerce de GA4'
          : 'Chiffre d\'affaires e-commerce de Matomo'
      };
    }

    switch (source?.source) {
      case 'GOOGLE_ANALYTICS':
        return { champ: 'conversionsValue', phrase: `Valeur de l'événement clé : ${nomEvenementCle(propriete?.evenementsCles)}` };
      case 'MATOMO':
        return {
          champ: 'conversionsValue',
          phrase: (propriete?.evenementsCles?.length
            ? 'Valeur de l\'objectif retenu'
            : 'Valeur des objectifs, tous objectifs confondus')
            + (ecommerceNonMesure(propriete)
              ? ' — le chiffre d\'affaires e-commerce n\'a pas pu être mesuré'
              : '')
        };
      case 'PIANO':
        return { champ: 'conversionsValue', phrase: 'Chiffre d\'affaires e-commerce de Piano' };
      default:
        return { champ: 'conversionsValue', phrase: null };
    }
  };

  /**
   * ⚠️ **`null` traverse, il n'est pas ramené à zéro.**
   *
   * C'est la contrepartie du `null` que le serveur rend pour une mesure qui n'a pas eu lieu :
   * le ramener à zéro ici referait le mensonge qu'il sert à éviter. Les appelants testent
   * `== null` et rendent une cellule vide. Un champ **absent** — Piano, qui n'a pas de mesure
   * e-commerce — passe par la même porte, et c'est juste : on n'en sait rien non plus.
   */
  const conversionsPropriete = (source, propriete, metrics) => {
    const valeur = metrics?.[origineConversions(source, propriete).champ];
    return valeur == null ? null : valeur;
  };

  const valeurPropriete = (source, propriete, metrics) => {
    const valeur = metrics?.[origineValeur(source, propriete).champ];
    return valeur == null ? null : valeur;
  };

  /**
   * Les totaux de la ligne agrégée, additionnés propriété par propriété.
   *
   * **Et non les totaux du serveur.** Il en rend deux familles — `conversions` d'un côté,
   * `transactions` de l'autre —, chacune sommée sur toutes les propriétés. Or la ligne doit
   * valoir la somme de ce que ses lignes de détail affichent, et celles-ci ne tirent pas
   * toutes du même champ. Lire un total du serveur ferait un en-tête qui ne s'accorde pas avec
   * son dépliant, sans que rien ne le signale.
   *
   * Rend `null` quand la source n'expose pas son détail : on retombe alors sur ses totaux,
   * plutôt que d'annoncer zéro.
   */
  const totauxAgreges = (source) => {
    const proprietes = source.sites || [];
    if (proprietes.length === 0) return null;

    const cumul = {
      conv: 0, convPrecedent: 0, valeur: 0, valeurPrecedente: 0,
      vendeuses: 0, surObjectif: 0, nonMesurees: 0
    };

    /*
     * Une propriété non mesurée n'entre dans aucun des deux camps : la classer « sur son
     * objectif » ferait dire au survol de la ligne de tête qu'elle ne vend pas, ce dont on ne
     * sait rien. Elle est comptée à part, et le survol l'annonce.
     */
    for (const propriete of proprietes) {
      const conv = conversionsPropriete(source, propriete, propriete.metrics);
      const convPrecedent = conversionsPropriete(source, propriete, propriete.previousPeriod);
      const valeur = valeurPropriete(source, propriete, propriete.metrics);
      const valeurPrecedente = valeurPropriete(source, propriete, propriete.previousPeriod);

      cumul.conv += conv || 0;
      cumul.convPrecedent += convPrecedent || 0;
      cumul.valeur += valeur || 0;
      cumul.valeurPrecedente += valeurPrecedente || 0;

      if (conv == null) cumul.nonMesurees += 1;
      else if (proprieteVend(propriete)) cumul.vendeuses += 1;
      else cumul.surObjectif += 1;
    }

    return cumul;
  };

  /**
   * La composition d'un total, dite au survol de la ligne agrégée.
   *
   * Quand toutes les propriétés sont du même côté, la phrase de ce côté suffit : répéter
   * « 5 propriétés sur leurs objectifs » n'apprend rien de plus que ce que la phrase dit déjà.
   */
  const compositionAgregee = (source, cumul, origine) => {
    const proprietes = source.sites || [];
    if (proprietes.length === 0) return null;

    // Ce que le total laisse de côté, faute de mesure. Dit en premier : c'est la réserve à
    // connaître avant de lire le chiffre, pas une précision de fin de phrase.
    const reserve = cumul.nonMesurees > 0
      ? ` — ${cumul.nonMesurees} propriété${cumul.nonMesurees > 1 ? 's' : ''} non mesurée${cumul.nonMesurees > 1 ? 's' : ''}, hors total`
      : '';

    if (cumul.vendeuses === 0 || cumul.surObjectif === 0) {
      return (origine(source, proprietes[0]).phrase || '') + reserve;
    }

    const nomsObjectifs = [...new Set(
      proprietes
        .filter(propriete => !proprieteVend(propriete))
        .map(propriete => origine(source, propriete).phrase)
    )];

    return `${cumul.vendeuses} propriété${cumul.vendeuses > 1 ? 's' : ''} e-commerce, `
      + `${cumul.surObjectif} sur ${cumul.surObjectif > 1 ? 'leurs objectifs' : 'son objectif'} `
      + `(${nomsObjectifs.join(' · ')})${reserve}`;
  };

  /**
   * La dépense de chaque compte publicitaire, par identifiant.
   *
   * Le tableau des plateformes porte les deux écritures d'un compte — interne et régie — sans
   * garantir laquelle il aura remplie. Les deux sont donc indexées, et le serveur rend les
   * deux de son côté : s'en tenir à une seule laisserait des comptes introuvables selon la
   * régie, en silence.
   */
  const depenseParCompte = useMemo(() => {
    const parCompte = new Map();
    for (const comptes of Object.values(platformAccountDetails || {})) {
      for (const compte of comptes) {
        const depense = { cout: compte.cost || 0, coutPrecedent: compte.previousPeriod?.cost || 0 };
        for (const cle of [compte.accountId, compte.sourceId, compte.id]) {
          if (cle) parCompte.set(String(cle), depense);
        }
      }
    }
    return parCompte;
  }, [platformAccountDetails]);

  /**
   * Le budget d'une propriété : celui que le dashboard consolidé lui montre.
   *
   * Le serveur dit quels comptes il croise avec elle (`comptesConsolide`), par la fonction même
   * qui décide du périmètre du consolidé. L'accueil se contentait auparavant des comptes rangés
   * sur son site, ce qui comptait en trop ceux que le site marque « hors consolidé » et ceux
   * d'un autre site — 10 510 € contre 9 538 € sur un compte réel, pour des conversions
   * pourtant identiques des deux côtés.
   *
   * Les coûts saisis à la main s'y ajoutent. Ils sont déclarés **sur la propriété**, pas sur un
   * compte : une propriété sans le moindre compte publicitaire en reçoit donc tout de même.
   *
   * Un même compte peut nourrir deux propriétés — deux propriétés d'un même site, par exemple.
   * C'est ce que le consolidé affiche pour chacune, et le reproduire est donc juste ligne à
   * ligne ; c'est la ligne de tête qui dédoublonne, plus bas.
   */
  const budgetParPropriete = useMemo(() => {
    const budgets = new Map();

    for (const source of analyticsData || []) {
      for (const propriete of source.sites || []) {
        let cout = propriete.coutSaisi || 0;
        let coutPrecedent = propriete.coutSaisiPrecedent || 0;
        let trouve = propriete.coutSaisi != null || propriete.coutSaisiPrecedent != null;

        // Dédoublonné : le serveur rend les deux écritures du même compte.
        const vus = new Set();
        for (const cle of propriete.comptesConsolide || []) {
          const depense = depenseParCompte.get(String(cle));
          if (!depense || vus.has(depense)) continue;
          vus.add(depense);
          cout += depense.cout;
          coutPrecedent += depense.coutPrecedent;
          trouve = true;
        }

        // Rien de rien : la cellule reste vide, jamais à zéro — un zéro se lirait comme une
        // dépense nulle et ferait passer un CPA absent pour un CPA parfait.
        if (trouve) budgets.set(`${source.source}|${propriete.id}`, { cout, coutPrecedent });
      }
    }

    return budgets;
  }, [analyticsData, depenseParCompte]);

  /**
   * Le budget d'une solution de mesure : l'union de ce que portent ses propriétés.
   *
   * **Une union, pas une somme.** Deux propriétés d'un même site croisent les mêmes comptes :
   * les additionner compterait leur dépense deux fois. Chaque compte n'entre donc qu'une fois,
   * et la ligne de tête reste au-dessus de chacun de ses enfants sans jamais les doubler.
   *
   * Le montant porté par la source elle-même vient des propriétés **combinées** : leurs coûts
   * saisis ne se répartissent entre leurs membres par aucune clé déclarée, et le serveur les
   * verse donc à ce niveau.
   */
  const budgetParSource = useMemo(() => {
    const budgets = new Map();

    for (const source of analyticsData || []) {
      let cout = source.coutSaisi || 0;
      let coutPrecedent = source.coutSaisiPrecedent || 0;
      let trouve = source.coutSaisi != null || source.coutSaisiPrecedent != null;

      const vus = new Set();
      // Les saisies sont rangées sous le site : deux propriétés d'un même site portent le même
      // montant, et l'additionner deux fois le doublerait. `groupeSaisie` dit lequel est lequel.
      const groupesComptes = new Set();

      for (const propriete of source.sites || []) {
        const groupe = propriete.groupeSaisie || propriete.id;
        if (!groupesComptes.has(groupe)) {
          groupesComptes.add(groupe);
          cout += propriete.coutSaisi || 0;
          coutPrecedent += propriete.coutSaisiPrecedent || 0;
          if (propriete.coutSaisi != null || propriete.coutSaisiPrecedent != null) trouve = true;
        }

        for (const cle of propriete.comptesConsolide || []) {
          const depense = depenseParCompte.get(String(cle));
          if (!depense || vus.has(depense)) continue;
          vus.add(depense);
          cout += depense.cout;
          coutPrecedent += depense.coutPrecedent;
          trouve = true;
        }
      }

      if (trouve) budgets.set(source.source, { cout, coutPrecedent });
    }

    return budgets;
  }, [analyticsData, depenseParCompte]);

  /**
   * Métrique d'une ligne de solution de mesure : sa valeur, son texte et sa variation.
   *
   * **Une seule source de vérité par colonne.** Le tri et l'affichage lisent le même
   * calcul : les séparer ferait classer sur un nombre et lire un autre, une incohérence
   * qu'aucune erreur ne signalerait.
   *
   * Partagée par le tableau Analytics et le tableau Consolidé : les deux montrent les mêmes
   * sessions croisées avec le même budget régies.
   *
   * Le budget, le CPA, le COS, le ROAS et le ROI viennent des totaux des régies : la
   * solution de mesure ne connaît pas la dépense, seule la page peut les croiser.
   */
  const metriqueAnalyticsSource = (source, metricKey) => {
    const prevSessions = source.previousPeriod?.sessions || 0;

    // Le total se compose depuis le détail, quelle que soit la solution de mesure : il doit
    // valoir la somme des lignes qu'on déplie sous lui. Voir `totauxAgreges`.
    const cumul = totauxAgreges(source);
    const convCount = cumul ? cumul.conv : (source.conversions || 0);
    const prevConvCount = cumul ? cumul.convPrecedent : (source.previousPeriod?.conversions || 0);
    const convValue = cumul ? cumul.valeur : (source.conversionsValue || 0);
    const prevValue = cumul ? cumul.valeurPrecedente : (source.previousPeriod?.conversionsValue || 0);
    const croissance = totals.calculateGrowthRate;

    /**
     * Le budget de la solution, tel que le consolidé le compose.
     *
     * Et non `totals.totalCost`, qui est la dépense de **toutes** les plateformes du filtre en
     * cours : il y entrait des comptes marqués « hors consolidé » et des comptes d'un autre
     * site que ceux de la propriété. Les quatre ratios ci-dessous s'y adossent, faute de quoi
     * ils diraient d'un budget ce que la ligne Budget juste au-dessus dit d'un autre.
     *
     * Le repli sur `totals` couvre le cas d'un serveur pas encore à jour : mieux vaut l'ancien
     * chiffre que des cellules vides.
     */
    const budgetSource = budgetParSource.get(source.source);
    const coutDeLaSource = budgetSource ? budgetSource.cout : totals.totalCost;
    const coutPrecedentDeLaSource = budgetSource ? budgetSource.coutPrecedent : totals.totalPreviousCost;

    switch (metricKey) {
      case 'sessions':
        return {
          valeur: source.sessions || 0,
          texte: formatNumber(source.sessions),
          variation: source.growthRates?.sessions
        };
      case 'cout':
        return {
          valeur: coutDeLaSource,
          texte: formatCurrency(coutDeLaSource),
          // La variation se refait sur les deux montants : reprendre celle des régies
          // comparerait un budget qui inclut les saisies à un écart qui les ignore.
          variation: budgetSource
            ? croissance(coutDeLaSource, coutPrecedentDeLaSource)
            : totals.growthRates?.cost,
          typeVariation: 'cost'
        };
      case 'taux_conv': {
        const taux = source.sessions > 0 ? ((convCount / source.sessions) * 100) : 0;
        const tauxPrecedent = prevSessions > 0 ? (prevConvCount / prevSessions) * 100 : 0;
        return { valeur: taux, texte: `${taux.toFixed(2)}%`, variation: croissance(taux, tauxPrecedent) };
      }
      // La variation se refait sur les deux totaux composés : les `growthRates` du serveur
      // portent sur ses propres sommes, qui ne sont pas celles affichées ici.
      case 'conv':
        return {
          valeur: convCount,
          texte: formatNumber(convCount, 0),
          variation: cumul ? croissance(convCount, prevConvCount) : source.growthRates?.conversions,
          infobulle: cumul ? compositionAgregee(source, cumul, origineConversions) : undefined
        };
      case 'valeur_conv':
        return {
          valeur: convValue,
          texte: formatCurrency(convValue),
          variation: cumul ? croissance(convValue, prevValue) : source.growthRates?.conversionsValue,
          infobulle: cumul ? compositionAgregee(source, cumul, origineValeur) : undefined
        };
      case 'cpa': {
        const cpa = convCount > 0 ? (coutDeLaSource / convCount) : 0;
        const cpaPrecedent = prevConvCount > 0 ? coutPrecedentDeLaSource / prevConvCount : 0;
        return {
          valeur: cpa,
          texte: formatCurrency(cpa),
          variation: croissance(cpa, cpaPrecedent),
          typeVariation: 'cpa'
        };
      }
      case 'cos': {
        const cos = convValue > 0 ? ((coutDeLaSource / convValue) * 100) : 0;
        const cosPrecedent = prevValue > 0 ? (coutPrecedentDeLaSource / prevValue) * 100 : 0;
        return {
          valeur: cos,
          texte: `${cos.toFixed(2)}%`,
          variation: croissance(cos, cosPrecedent),
          typeVariation: 'cos'
        };
      }
      case 'panier_moyen': {
        const panier = convCount > 0 ? (convValue / convCount) : 0;
        const panierPrecedent = prevConvCount > 0 ? prevValue / prevConvCount : 0;
        return { valeur: panier, texte: formatCurrency(panier), variation: croissance(panier, panierPrecedent) };
      }
      case 'roas': {
        const roas = coutDeLaSource > 0 ? (convValue / coutDeLaSource) : 0;
        const roasPrecedent = coutPrecedentDeLaSource > 0 ? prevValue / coutPrecedentDeLaSource : 0;
        return { valeur: roas, texte: `${(roas * 100).toFixed(0)}%`, variation: croissance(roas, roasPrecedent) };
      }
      case 'roi': {
        const roi = coutDeLaSource > 0 ? ((convValue - coutDeLaSource) / coutDeLaSource) : 0;
        const roiPrecedent = coutPrecedentDeLaSource > 0 ? (prevValue - coutPrecedentDeLaSource) / coutPrecedentDeLaSource : 0;
        return { valeur: roi, texte: roi.toFixed(2), variation: croissance(roi, roiPrecedent) };
      }
      default:
        return CELLULE_VIDE;
    }
  };

  /**
   * Métrique d'une ligne de compte rattaché à une solution de mesure.
   *
   * Les métriques qui supposent une dépense étaient vides ici : le budget des régies n'était
   * connu qu'au global, et le répartir entre les propriétés aurait inventé un chiffre. Le site
   * a levé cet obstacle — il dit lui-même quelles régies alimentent quelle propriété —, donc
   * elles se remplissent **quand le rattachement est univoque**, et restent vides sinon. La
   * prudence d'origine n'a pas changé de nature, elle s'applique seulement là où le doute
   * subsiste.
   */
  const metriqueAnalyticsSite = (source, site, metricKey) => {
    const sessions = site.metrics?.sessions || 0;
    const convCount = conversionsPropriete(source, site, site.metrics);
    const prevConvCount = conversionsPropriete(source, site, site.previousPeriod);
    const value = valeurPropriete(source, site, site.metrics);
    const prevValue = valeurPropriete(source, site, site.previousPeriod);
    const croissance = totals.calculateGrowthRate;
    const depense = budgetParPropriete.get(`${source.source}|${site.id}`);

    switch (metricKey) {
      case 'sessions':
        return { valeur: sessions, texte: formatNumber(sessions), variation: site.growthRates?.sessions };
      case 'taux_conv': {
        if (convCount == null) return cellulePasMesuree(origineConversions(source, site).phrase);
        const taux = sessions > 0 ? ((convCount / sessions) * 100) : 0;
        // Le serveur ne calcule cette variation que sur `conversions`, et la propriété ne
        // dénombre pas forcément cela : on la refait sur le dénombrement affiché.
        const prevSessions = site.previousPeriod?.sessions || 0;
        const variation = prevConvCount == null
          ? null
          : croissance(taux, prevSessions > 0 ? (prevConvCount / prevSessions) * 100 : 0);
        return { valeur: taux, texte: `${taux.toFixed(2)}%`, variation };
      }
      case 'conv':
        if (convCount == null) return cellulePasMesuree(origineConversions(source, site).phrase);
        return {
          valeur: convCount,
          texte: formatNumber(convCount, 0),
          // La variation se refait sur le dénombrement affiché : les `growthRates` du serveur
          // portent sur `conversions`, que la propriété ne lit pas forcément.
          variation: prevConvCount == null ? null : croissance(convCount, prevConvCount),
          infobulle: origineConversions(source, site).phrase || undefined
        };
      case 'valeur_conv':
        if (value == null) return cellulePasMesuree(origineValeur(source, site).phrase);
        return {
          valeur: value,
          texte: formatCurrency(value),
          variation: prevValue == null ? null : croissance(value, prevValue),
          infobulle: origineValeur(source, site).phrase || undefined
        };
      case 'panier_moyen': {
        if (convCount == null || value == null) return cellulePasMesuree();
        const panier = convCount > 0 ? (value / convCount) : 0;
        const panierPrecedent = (prevConvCount == null || prevValue == null || prevConvCount === 0)
          ? null
          : prevValue / prevConvCount;
        return {
          valeur: panier,
          texte: formatCurrency(panier),
          variation: panierPrecedent == null ? null : croissance(panier, panierPrecedent)
        };
      }
      case 'cout':
      case 'cpa':
      case 'cos':
      case 'roas':
      case 'roi': {
        // Sans dépense héritable, la cellule reste vide. Un zéro s'y lirait comme « rien
        // dépensé », et ferait passer un CPA absent pour un CPA parfait.
        if (!depense) return CELLULE_VIDE;

        if (metricKey === 'cout') {
          return {
            valeur: depense.cout,
            texte: formatCurrency(depense.cout),
            variation: croissance(depense.cout, depense.coutPrecedent),
            typeVariation: 'cost'
          };
        }
        if (metricKey === 'cpa') {
          const cpa = convCount > 0 ? depense.cout / convCount : 0;
          const cpaPrecedent = prevConvCount > 0 ? depense.coutPrecedent / prevConvCount : 0;
          return {
            valeur: cpa, texte: formatCurrency(cpa),
            variation: croissance(cpa, cpaPrecedent), typeVariation: 'cpa'
          };
        }
        if (metricKey === 'cos') {
          const cos = value > 0 ? (depense.cout / value) * 100 : 0;
          const cosPrecedent = prevValue > 0 ? (depense.coutPrecedent / prevValue) * 100 : 0;
          return {
            valeur: cos, texte: `${cos.toFixed(2)}%`,
            variation: croissance(cos, cosPrecedent), typeVariation: 'cos'
          };
        }
        if (metricKey === 'roas') {
          const roas = depense.cout > 0 ? value / depense.cout : 0;
          const roasPrecedent = depense.coutPrecedent > 0 ? prevValue / depense.coutPrecedent : 0;
          return { valeur: roas, texte: `${(roas * 100).toFixed(0)}%`, variation: croissance(roas, roasPrecedent) };
        }
        const roi = depense.cout > 0 ? (value - depense.cout) / depense.cout : 0;
        const roiPrecedent = depense.coutPrecedent > 0 ? (prevValue - depense.coutPrecedent) / depense.coutPrecedent : 0;
        return { valeur: roi, texte: roi.toFixed(2), variation: croissance(roi, roiPrecedent) };
      }
      default:
        return CELLULE_VIDE;
    }
  };

  /**
   * Ce que compte « Conv. » sur un compte Meta, dit au survol.
   *
   * La métrique Conversions de Meta n'est plus forcément les achats : l'utilisateur choisit ses
   * sources dans le dashboard, et la ligne Facebook Ads les compte désormais comme lui. Rien à
   * l'écran ne disait lesquelles — deux comptes voisins affichaient la même colonne sur deux
   * mesures différentes, sans que rien ne les distingue.
   *
   * `null` sur les autres régies, et sur un compte Meta qui n'a rien configuré : le nombre y
   * compte les achats, ce que la colonne annonce déjà.
   */
  const sourcesDuCompte = (account) => {
    const sources = account?.conversionSources;
    if (!sources || sources.length === 0) return null;
    return sources.length === 1
      ? `Source comptée : ${sources[0]}`
      : `${sources.length} sources comptées :\n• ${sources.join('\n• ')}`;
  };

  /**
   * Ce que compte « Conv. » sur une ligne de régie, qui agrège plusieurs comptes.
   *
   * ⚠️ **Les comptes d'une même régie ne comptent pas forcément la même chose.** Additionner
   * leurs conversions reste juste — ce sont des conversions —, mais annoncer les sources de
   * l'un pour le total serait faux. Quand ils divergent, la bulle le dit compte par compte
   * plutôt que de fondre les listes en une seule, qui laisserait croire que chaque compte
   * remonte chacune d'elles.
   */
  const sourcesDeLaPlateforme = (platformKey) => {
    const comptes = (platformAccountDetails?.[platformKey] || [])
      .filter(compte => compte.conversionSources?.length > 0);
    if (comptes.length === 0) return null;

    const listes = [...new Set(comptes.map(compte => compte.conversionSources.join(' • ')))];
    if (listes.length === 1) {
      const sources = comptes[0].conversionSources;
      const tete = comptes.length === 1
        ? ''
        : `${comptes.length} comptes, mêmes sources.\n`;
      return tete + (sources.length === 1
        ? `Source comptée : ${sources[0]}`
        : `${sources.length} sources comptées :\n• ${sources.join('\n• ')}`);
    }

    return 'Sources différentes selon le compte :\n'
      + comptes.map(compte => `• ${compte.accountName || compte.name} : ${compte.conversionSources.join(', ')}`).join('\n');
  };

  /** Métrique d'une ligne de régie du tableau Plateformes. */
  const metriquePlateforme = (platform, metricKey) => {
    const precedent = platform.previousPeriod || {};
    const variation = (courant, ancien) => (
      ancien > 0 ? ((courant - ancien) / ancien) * 100 : (courant > 0 ? DEPUIS_ZERO : null)
    );

    switch (metricKey) {
      case 'clics':
        return { valeur: platform.clicks || 0, texte: formatNumber(platform.clicks), variation: platform.growthRates?.clicks };
      case 'cout':
        return {
          valeur: platform.cost || 0,
          texte: formatCurrency(platform.cost),
          variation: platform.growthRates?.cost,
          typeVariation: 'cost'
        };
      case 'taux_conv': {
        const taux = platform.clicks > 0 ? ((platform.conversions / platform.clicks) * 100) : 0;
        const prevClics = precedent.clicks || 0;
        const tauxPrecedent = prevClics > 0 ? ((precedent.conversions || 0) / prevClics) * 100 : 0;
        // Le taux se calcule sur ces conversions-là : la même mention qu'à la colonne Conv.
        return {
          valeur: taux,
          texte: `${taux.toFixed(2)}%`,
          variation: variation(taux, tauxPrecedent),
          infobulle: sourcesDeLaPlateforme(platform.platform)
        };
      }
      case 'conv':
        return {
          valeur: platform.conversions || 0,
          texte: formatNumber(platform.conversions, 1),
          variation: platform.growthRates?.conversions,
          infobulle: sourcesDeLaPlateforme(platform.platform)
        };
      case 'valeur_conv':
        return {
          valeur: platform.conversionValue || 0,
          texte: formatCurrency(platform.conversionValue),
          variation: platform.growthRates?.conversionValue
        };
      case 'cpa':
        return {
          valeur: platform.cpa || 0,
          texte: formatCPA(platform.cpa || 0),
          variation: platform.growthRates?.cpa,
          typeVariation: 'cpa'
        };
      case 'cos':
        return {
          valeur: platform.cos || 0,
          texte: `${(platform.cos || 0).toFixed(0)}%`,
          variation: platform.growthRates?.cos,
          typeVariation: 'cos'
        };
      case 'panier_moyen': {
        const panier = platform.conversions > 0 ? (platform.conversionValue / platform.conversions) : 0;
        const prevConv = precedent.conversions || 0;
        const panierPrecedent = prevConv > 0 ? (precedent.conversionValue || 0) / prevConv : 0;
        return {
          valeur: panier,
          texte: formatCurrency(panier),
          variation: panierPrecedent > 0 ? ((panier - panierPrecedent) / panierPrecedent) * 100 : null
        };
      }
      case 'roas': {
        const roas = platform.cost > 0 ? (platform.conversionValue / platform.cost) : 0;
        const prevCout = precedent.cost || 0;
        const roasPrecedent = prevCout > 0 ? (precedent.conversionValue || 0) / prevCout : 0;
        return {
          valeur: roas,
          texte: `${(roas * 100).toFixed(0)}%`,
          variation: roasPrecedent > 0 ? ((roas - roasPrecedent) / roasPrecedent) * 100 : null
        };
      }
      case 'roi': {
        const roi = platform.cost > 0 ? ((platform.conversionValue - platform.cost) / platform.cost) : 0;
        const prevCout = precedent.cost || 0;
        const roiPrecedent = prevCout > 0 ? ((precedent.conversionValue || 0) - prevCout) / prevCout : 0;
        return {
          valeur: roi,
          texte: roi.toFixed(2),
          variation: roiPrecedent !== 0 ? ((roi - roiPrecedent) / Math.abs(roiPrecedent)) * 100 : null
        };
      }
      default:
        return CELLULE_VIDE;
    }
  };

  /**
   * Métrique d'une ligne de compte du tableau Plateformes.
   *
   * Un compte porte les mêmes champs qu'une régie à deux noms près (`conversionsValue` au
   * lieu de `conversionValue`), d'où cette fonction jumelle plutôt qu'un renommage à la
   * volée qui masquerait la différence.
   */
  const metriqueCompte = (account, metricKey) => {
    const precedent = account.previousPeriod || {};
    const variation = (courant, ancien) => (
      ancien > 0 ? ((courant - ancien) / ancien) * 100 : (courant > 0 ? DEPUIS_ZERO : null)
    );

    switch (metricKey) {
      case 'clics':
        return { valeur: account.clicks || 0, texte: formatNumber(account.clicks || 0), variation: account.growthRates?.clicks };
      case 'cout':
        return {
          valeur: account.cost || 0,
          texte: formatCurrency(account.cost || 0),
          variation: account.growthRates?.cost,
          typeVariation: 'cost'
        };
      case 'taux_conv': {
        const taux = account.clicks > 0 ? (((account.conversions || 0) / account.clicks) * 100) : 0;
        const prevClics = precedent.clicks || 0;
        const tauxPrecedent = prevClics > 0 ? ((precedent.conversions || 0) / prevClics) * 100 : 0;
        // Le taux se calcule sur ces conversions-là : la même mention qu'à la colonne Conv.
        return {
          valeur: taux,
          texte: `${taux.toFixed(2)}%`,
          variation: variation(taux, tauxPrecedent),
          infobulle: sourcesDuCompte(account)
        };
      }
      case 'conv':
        return {
          valeur: account.conversions || 0,
          texte: formatNumber(account.conversions || 0, 1),
          variation: account.growthRates?.conversions,
          infobulle: sourcesDuCompte(account)
        };
      case 'valeur_conv':
        return {
          valeur: account.conversionsValue || 0,
          texte: formatCurrency(account.conversionsValue || 0),
          variation: account.growthRates?.conversionsValue
        };
      case 'cpa':
        return {
          valeur: account.cpa || 0,
          texte: formatCPA(account.cpa || 0),
          variation: account.growthRates?.cpa,
          typeVariation: 'cpa'
        };
      case 'cos':
        return {
          valeur: account.cos || 0,
          texte: `${(account.cos || 0).toFixed(0)}%`,
          variation: account.growthRates?.cos,
          typeVariation: 'cos'
        };
      case 'panier_moyen': {
        const conv = account.conversions || 0;
        const panier = conv > 0 ? ((account.conversionsValue || 0) / conv) : 0;
        const prevConv = precedent.conversions || 0;
        const panierPrecedent = prevConv > 0 ? (precedent.conversionsValue || 0) / prevConv : 0;
        return {
          valeur: panier,
          texte: formatCurrency(panier),
          variation: panierPrecedent > 0 ? ((panier - panierPrecedent) / panierPrecedent) * 100 : null
        };
      }
      case 'roas': {
        const cout = account.cost || 0;
        const roas = cout > 0 ? ((account.conversionsValue || 0) / cout) : 0;
        const prevCout = precedent.cost || 0;
        const roasPrecedent = prevCout > 0 ? (precedent.conversionsValue || 0) / prevCout : 0;
        return {
          valeur: roas,
          texte: `${(roas * 100).toFixed(0)}%`,
          variation: roasPrecedent > 0 ? ((roas - roasPrecedent) / roasPrecedent) * 100 : null
        };
      }
      case 'roi': {
        const cout = account.cost || 0;
        const roi = cout > 0 ? (((account.conversionsValue || 0) - cout) / cout) : 0;
        const prevCout = precedent.cost || 0;
        const roiPrecedent = prevCout > 0 ? ((precedent.conversionsValue || 0) - prevCout) / prevCout : 0;
        return {
          valeur: roi,
          texte: roi.toFixed(2),
          variation: roiPrecedent !== 0 ? ((roi - roiPrecedent) / Math.abs(roiPrecedent)) * 100 : null
        };
      }
      default:
        return CELLULE_VIDE;
    }
  };

  /** Contenu d'une cellule : le chiffre, puis sa variation quand elle existe. */
  /**
   * Une cellule de métrique.
   *
   * `infobulle` sert aux colonnes dont le nombre ne se comprend pas seul : « Conv. » et
   * « Valeur Conv. » ne tirent pas du même champ selon la propriété, et le survol est le seul
   * endroit où l'on peut le dire sans alourdir le tableau.
   *
   * La bulle sort par un portail, posée depuis les bords de la cellule : les tableaux rognent
   * ce qui dépasse de leurs lignes. `pointer-events: none` sur `.kbulle` évite qu'elle vole le
   * pointeur et clignote.
   *
   * La position horizontale est bornée pour que la bulle — 340 px au minimum — tienne dans la
   * fenêtre : les colonnes de droite sont étroites, et la poser sur leur bord gauche la ferait
   * sortir de l'écran.
   */
  const survolerMention = (texte) => (evenement) => {
    const zone = evenement.currentTarget.getBoundingClientRect();
    setMentionSurvolee({
      texte,
      x: Math.max(12, Math.min(zone.left, window.innerWidth - 340 - 12)),
      bas: zone.bottom + 6,
      haut: zone.top - 6
    });
  };

  const cellule = (metrique) => {
    const contenu = (
      <>
        {metrique.texte}
        {formatGrowthRate(metrique.variation, metrique.typeVariation || 'default')}
      </>
    );

    if (!metrique.infobulle) return contenu;

    return (
      <span
        style={{ cursor: 'help' }}
        onMouseEnter={survolerMention(metrique.infobulle)}
        onMouseLeave={() => setMentionSurvolee(null)}
      >
        {contenu}
      </span>
    );
  };

  /**
   * Lignes de tête des trois tableaux, dans l'ordre demandé.
   *
   * Le classement passe par la **même fonction que l'affichage** : trier sur un CPA
   * recalculé à côté finirait par classer sur un nombre que la cellule ne montre pas.
   */
  const triAnalyticsApplique = triEffectif(triAnalytics, analyticsMetricsConfig.order);
  const triPlateformesApplique = triEffectif(triPlateformes, platformsMetricsConfig.order);

  const sourcesAnalytics = trier(analyticsData, triAnalyticsApplique, (source, cle) => (
    cle === COLONNE_NOM ? getAnalyticsSourceName(source.source) : metriqueAnalyticsSource(source, cle).valeur
  ));

  const plateformesTriees = trier(platformData, triPlateformesApplique, (platform, cle) => (
    cle === COLONNE_NOM ? getPlatformName(platform.platform) : metriquePlateforme(platform, cle).valeur
  ));

  /** Comptes d'une solution de mesure, classés comme le tableau qui les contient. */
  const comptesTries = (source, tri) => trier(source.sites, tri, (site, cle) => (
    cle === COLONNE_NOM ? (site.name || '') : metriqueAnalyticsSite(source, site, cle).valeur
  ));

  /** Comptes d'une régie, classés comme le tableau Plateformes. */
  const comptesRegieTries = (comptes) => trier(comptes, triPlateformesApplique, (account, cle) => (
    cle === COLONNE_NOM ? (account.accountName || '') : metriqueCompte(account, cle).valeur
  ));

  /**
   * Les dashboards sur mesure à proposer sur l'accueil.
   *
   * ⚠️ **Aucun filtre ici, et c'est voulu.** La liste arrive bornée deux fois : par le serveur
   * — périmètre, droit client et `visibleClient` —, puis par le tamis du client choisi dans
   * l'en-tête (`dansLePerimetreCourant`, dans `App.js`), que seul l'appelant connaît. Un
   * troisième verdict écrit ici serait le premier à diverger.
   */
  const dashboardsSurMesureVisibles = dashboardsSurMesure || [];

  /**
   * Aucune activité à résumer : ni tableau de plateformes, ni tableau analytics.
   *
   * ⚠️ **Ce n'est pas « l'écran est vide », et les confondre a produit deux défauts
   * successifs.** Celui-ci gouverne le sous-titre « Activité des N derniers jours vs … », qui
   * annonce une **comparaison chiffrée** : les dashboards sur mesure n'en sont pas, ils
   * portent leur propre contenu. Les y compter faisait annoncer une activité comparée
   * au-dessus d'une simple liste de liens, avec un sélecteur de comparaison qui ne gouvernait
   * rien de visible.
   *
   * ⚠️ **Trois états, pas deux**, et c'est le piège qu'un booléen ne peut pas porter : « en
   * cours de chargement » n'est ni « il y a de l'activité » ni « il n'y en a pas ». Le verdict
   * négatif attendait bien la fin des chargements, mais le sous-titre se rendait sur sa
   * négation — donc **pendant** le chargement, où elle est vraie faute de savoir. Sur un accès
   * client dont les deux tableaux reviennent vides, « Activité des 7 derniers jours vs année
   * précédente » s'affichait puis disparaissait. Les deux verdicts sont désormais faux tant
   * qu'on ne sait pas : ni sous-titre, ni bloc d'explication, et rien ne se retire sous les
   * yeux du lecteur.
   */
  const chargementTermine = !loadingTableData && !loadingAnalyticsData;

  /** Il y a quelque chose à résumer — le verdict **positif**, celui que le sous-titre suit. */
  const ilYAUneActiviteAResumer = chargementTermine
    && ((platformData && platformData.length > 0)
      || (analyticsData && analyticsData.length > 0));

  const aucuneActiviteAResumer = chargementTermine && !ilYAUneActiviteAResumer;

  /**
   * L'écran n'a **rien** à montrer — pas même un dashboard sur mesure.
   *
   * ⚠️ Dérivé du précédent plutôt que réécrit : ce sont deux questions, mais la seconde
   * contient la première, et deux calculs parallèles divergeraient. Les dashboards sur mesure
   * comptent ici — sans quoi un client à qui l'agence n'en ouvre qu'un lisait « Votre agence
   * ne vous a ouvert aucun tableau de bord » juste au-dessus de celui qu'elle lui avait
   * ouvert.
   */
  const ecranSansDonnees = aucuneActiviteAResumer && dashboardsSurMesureVisibles.length === 0;

  return (
    <div className="dashboard-content" style={{ paddingBottom: '60px' }}>
      {/* En-tête avec titre et sélecteurs */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '2rem', fontWeight: '700', color: '#1e293b' }}>
            Bonjour {user?.name ? user.name.split(' ')[0] : ''}
          </h2>
          {/*
            ⚠️ **Pas de sous-titre quand il n'y a aucune activité à résumer.** « Activité des
            7 derniers jours vs année précédente » annonce une comparaison chiffrée : au-dessus
            d'un écran sans tableau — ou ne portant que des dashboards sur mesure, qui ont leur
            propre contenu —, elle décrit ce qui n'est pas là, et son sélecteur propose de
            régler une comparaison qui ne gouverne rien de visible.
          */}
          {ilYAUneActiviteAResumer && (
          <p style={{ margin: '8px 0 0 0', color: '#64748b', fontSize: '1rem' }}>
            Activité {(() => {
              switch (selectedPeriod) {
                case 'TODAY': return "d'aujourd'hui";
                case 'YESTERDAY': return "d'hier";
                case 'LAST_7_DAYS': return 'des 7 derniers jours';
                case 'LAST_14_DAYS': return 'des 14 derniers jours';
                case 'LAST_30_DAYS': return 'des 30 derniers jours';
                case 'LAST_90_DAYS': return 'des 90 derniers jours';
                case 'LAST_180_DAYS': return 'des 180 derniers jours';
                case 'LAST_365_DAYS': return 'des 365 derniers jours';
                case 'THIS_MONTH': return 'de ce mois';
                case 'LAST_MONTH': return 'du mois dernier';
                case 'THIS_YEAR': return "de l'année en cours";
                case 'LAST_YEAR': return "de l'année dernière";
                case 'CUSTOM': return customStartDate && customEndDate ? `du ${customStartDate} au ${customEndDate}` : '';
                default: return 'des 7 derniers jours';
              }
            })()} vs{' '}
            {/*
              La base de comparaison, choisie dans la phrase plutôt qu'à côté.

              Le titre annonçait « vs période précédente » sans que rien ne soit réglable : la
              mention décrivait un choix qui n'en était pas un. Le sélecteur prend sa place, au
              mot près, pour que la phrase se lise toujours d'un trait.

              Dessiné comme du texte — pas de cadre, pas de flèche de liste — afin de ne pas
              transformer un titre en formulaire. Le soulignement pointillé signale qu'il y a
              là quelque chose à ouvrir.
            */}
            <select
              value={baseComparaison}
              onChange={(e) => setBaseComparaison(e.target.value)}
              aria-label="Base de comparaison"
              style={{
                appearance: 'none', WebkitAppearance: 'none',
                border: 'none', background: 'transparent', padding: 0,
                font: 'inherit', color: '#475569', fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline dotted',
                textUnderlineOffset: '3px'
              }}
            >
              <option value="previous_period">période précédente</option>
              <option value="previous_year">année précédente</option>
            </select>
          </p>
          )}
        </div>
        
        {/* Sélecteurs en haut à droite */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Bouton Refresh Cache - LOCAL UNIQUEMENT */}
          {config.IS_LOCAL && user?.role === 'ADMIN' && (
            <button
              onClick={handleRefreshCache}
              disabled={refreshingCache}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 12px',
                backgroundColor: refreshingCache ? '#94a3b8' : '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: refreshingCache ? 'not-allowed' : 'pointer'
              }}
              title="Recalculer le cache des widgets (local uniquement)"
            >
              <RefreshCw size={16} className={refreshingCache ? 'animate-spin' : ''} />
              {refreshingCache ? 'Calcul...' : 'Refresh Cache'}
            </button>
          )}

        </div>
      </div>

      {/* Message d'invitation à connecter un connecteur ou ajouter un compte si aucune donnée */}
      {ecranSansDonnees && (
        <div style={{
          backgroundColor: '#FAF9F5',
          border: '1px solid #e1e8ed',
          borderRadius: '12px',
          padding: '48px 40px',
          textAlign: 'center',
          marginBottom: '24px'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#1a1a1a',
            borderRadius: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px'
          }}>
            {estAccesClient
              ? <Lock size={32} color="white" />
              : hasActiveConnectors ? <Package size={32} color="white" /> : <BarChart3 size={32} color="white" />}
          </div>
          <h3 style={{ margin: '0 0 12px', fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>
            {estAccesClient
              ? 'Aucune donnée à afficher'
              : hasActiveConnectors ? 'Ajoutez un compte' : 'Connectez vos données'}
          </h3>
          <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: '15px', lineHeight: '1.6', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
            {estAccesClient
              ? "Votre agence ne vous a ouvert aucun tableau de bord pour le moment. Contactez-la si vous pensez qu'il s'agit d'une erreur."
              : hasActiveConnectors
                ? 'Vos sources sont connectées. Ajoutez maintenant un compte publicitaire pour visualiser vos performances.'
                : 'Connectez Google Ads, Meta Ads, Google Analytics ou d\'autres sources pour visualiser vos performances.'}
          </p>
          {/*
            ⚠️ **Aucun bouton pour un accès client : il ouvrirait une impasse.**

            Un client ne connecte rien et n'ajoute aucun compte — il reçoit ce que son agence
            lui ouvre. Le bouton le menait à l'écran Connecteurs, où il n'a pas sa place, et
            c'est de l'outil qu'il aurait conclu qu'il est cassé. Même raison qui fait que
            l'Optimiseur de flux ne propose pas à un client d'analyser un flux.
          */}
          {!estAccesClient && (
          <button
            onClick={() => {
              if (hasActiveConnectors) {
                setOpenAddModalOnLibraryLoad(true);
                setCurrentView('library');
              } else {
                setOpenAddConnectorModalOnLoad(true);
                setCurrentView('connectors');
              }
            }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              backgroundColor: '#1a1a1a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            {hasActiveConnectors ? <Plus size={18} /> : <BarChart3 size={18} />}
            {hasActiveConnectors ? 'Ajouter un compte' : 'Connecter une source'}
          </button>
          )}
        </div>
      )}


      {/* Tableau de synthèse Analytics */}
      {loadingAnalyticsData ? (
        bandeauDeChargement('Chargement des Analytics...', 'Récupération des données Analytics')
      ) : sourcesAnalytics && sourcesAnalytics.length > 0 ? (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e2e8f0',
          marginBottom: '24px'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9f7f4' }}>
                <EnteteTriable
                  tri={triAnalyticsApplique}
                  definirTri={setTriAnalytics}
                  cle={COLONNE_NOM}
                  alignement="left"
                  style={{ width: '16%' }}
                >
                  <BarChart2 size={16} />
                  <span>Analytics</span>
                  {user?.role !== 'CLIENT' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowAnalyticsMetricsConfig(true);
                      }}
                      className="metrics-settings-btn"
                      title="Configurer les colonnes"
                    >
                      <Settings size={16} />
                    </button>
                  )}
                </EnteteTriable>
                {analyticsMetricsConfig.order.map((metricKey) => (
                  <EnteteTriable
                    key={metricKey}
                    tri={triAnalyticsApplique}
                    definirTri={setTriAnalytics}
                    cle={metricKey}
                    style={{ width: metricKey === 'valeur_conv' ? '11%' : '9%' }}
                  >
                    {libelleColonne(metricKey)}
                  </EnteteTriable>
                ))}
                <th style={{
                  padding: '16px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  color: '#374151',
                  borderBottom: '1px solid #e2e8f0',
                  width: '10%'
                }}>
                  Détails
                </th>
              </tr>
            </thead>
            <tbody>
              {sourcesAnalytics.map((source, index) => {
                const isExpanded = expandedAnalyticsSources.has(source.source);
                const sites = comptesTries(source, triAnalyticsApplique);

                return (
                  <React.Fragment key={source.source || index}>
                    {/* Ligne principale de la source */}
                    <tr
                      style={{
                        borderBottom: (!isExpanded && index < sourcesAnalytics.length - 1) ? '1px solid #f1f5f9' : 'none',
                        transition: 'background-color 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onClick={() => toggleAnalyticsSourceExpansion(source.source)}
                      className="homepage-table-row"
                    >
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}>
                            ▶
                          </span>
                          <span style={{ fontSize: '1.25rem' }}>
                            {getAnalyticsIcon(source.source)}
                          </span>
                          <span style={{ fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>
                            {getAnalyticsSourceName(source.source)}
                          </span>
                        </div>
                      </td>
                      {/* Cellules dynamiques selon la configuration des métriques */}
                      {analyticsMetricsConfig.order.map((metricKey) => (
                        <td key={metricKey} style={{
                          padding: '16px 20px',
                          textAlign: 'center',
                          fontWeight: '500',
                          fontSize: '0.875rem',
                          color: '#374151'
                        }}>
                          <div>{cellule(metriqueAnalyticsSource(source, metricKey))}</div>
                        </td>
                      ))}
                      <td style={{
                        padding: '16px 20px',
                        textAlign: 'center'
                      }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewAnalyticsDashboard(source.source);
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#1a1a1a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#333333'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#1a1a1a'}
                        >
                          Voir
                        </button>
                      </td>
                    </tr>

                    {/* Lignes détaillées des sites */}
                    {isExpanded && sites.map((site, siteIndex) => (
                      <tr
                        key={`${source.source}-${site.id || siteIndex}`}
                        className="homepage-table-row"
                        style={{
                          backgroundColor: 'white',
                          borderBottom: siteIndex === sites.length - 1 && index < sourcesAnalytics.length - 1 ? '1px solid #f1f5f9' : '1px solid #e2e8f0'
                        }}
                      >
                        <td style={{ padding: '12px 20px 12px 60px', wordBreak: 'break-word' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ fontWeight: '500', color: '#374151', fontSize: '0.875rem', wordBreak: 'break-word' }}>
                              {site.name || 'Site sans nom'}
                            </span>
                            {site.instanceUrl && (
                              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                {site.instanceUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                              </span>
                            )}
                          </div>
                        </td>
                        {/* Cellules dynamiques selon la configuration des métriques */}
                        {analyticsMetricsConfig.order.map((metricKey) => (
                          <td key={metricKey} style={{
                            padding: '12px 20px',
                            textAlign: 'center',
                            fontSize: '0.875rem',
                            color: ['cout', 'cpa', 'cos', 'roas', 'roi'].includes(metricKey) ? '#9ca3af' : '#374151'
                          }}>
                            <div>{cellule(metriqueAnalyticsSite(source, site, metricKey))}</div>
                          </td>
                        ))}
                        <td style={{
                          padding: '12px 20px',
                          textAlign: 'center'
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewAnalyticsDashboard(source.source, site.id);
                            }}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#1a1a1a',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#333333'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#1a1a1a'}
                          >
                            Voir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Tableau de synthèse par plateforme */}
      {loadingTableData ? (
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '300px',
          textAlign: 'center', 
          padding: '40px',
          backgroundColor: 'white', 
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <div className="loading-spinner" />
          <h3 style={{ margin: '16px 0 8px 0' }}>Chargement de la synthèse...</h3>
          <p style={{ margin: 0, color: '#64748b' }}>Agrégation des données de toutes vos plateformes</p>
        </div>
      ) : error ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          backgroundColor: '#fee2e2', 
          borderRadius: '12px', 
          border: '1px solid #fecaca',
          color: '#dc2626' 
        }}>
          <h4 style={{ margin: '0 0 8px 0' }}>Erreur de chargement</h4>
          <p style={{ margin: '0 0 16px 0' }}>{error}</p>
          <button 
            onClick={loadPlatformData}
            style={{
              padding: '8px 16px',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            Réessayer
          </button>
        </div>
      ) : !platformData || platformData.length === 0 ? (
        null
      ) : (
        <div style={{ 
          backgroundColor: 'white', 
          borderRadius: '12px', 
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9f7f4' }}>
                <EnteteTriable
                  tri={triPlateformesApplique}
                  definirTri={setTriPlateformes}
                  cle={COLONNE_NOM}
                  alignement="left"
                  style={{ width: '16%' }}
                >
                  <Database size={16} />
                  <span>Publicité</span>
                  {user?.role !== 'CLIENT' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowPlatformsMetricsConfig(true);
                      }}
                      className="metrics-settings-btn"
                      title="Configurer les colonnes"
                    >
                      <Settings size={16} />
                    </button>
                  )}
                </EnteteTriable>
                {platformsMetricsConfig.order.map((metricKey) => (
                  <EnteteTriable
                    key={metricKey}
                    tri={triPlateformesApplique}
                    definirTri={setTriPlateformes}
                    cle={metricKey}
                    style={{ width: metricKey === 'valeur_conv' ? '11%' : '9%' }}
                  >
                    {libelleColonne(metricKey)}
                  </EnteteTriable>
                ))}
                <th style={{
                  padding: '16px 8px',
                  textAlign: 'center',
                  fontWeight: '600',
                  fontSize: '0.8rem',
                  color: '#374151',
                  borderBottom: '1px solid #e2e8f0',
                  width: '10%'
                }}>
                  Détails
                </th>
              </tr>
            </thead>
            <tbody>
              {plateformesTriees.map((platform, index) => {
                const isExpanded = expandedPlatforms.has(platform.platform);
                const accounts = comptesRegieTries(platformAccountDetails[platform.platform] || []);
                
                return (
                  <React.Fragment key={platform.platform || index}>
                    {/* Ligne principale de la plateforme */}
                    <tr 
                      style={{ 
                        borderBottom: (!isExpanded && index < plateformesTriees.length - 1) ? '1px solid #f1f5f9' : 'none',
                        transition: 'background-color 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onClick={() => togglePlatformExpansion(platform.platform)}
                      className="homepage-table-row"
                    >
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease'
                          }}>
                            ▶
                          </span>
                          <span style={{ fontSize: '1.25rem' }}>
                            {getPlatformIcon(platform.platform)}
                          </span>
                          <span style={{ fontWeight: '500', fontSize: '0.875rem', color: '#374151' }}>
                            {getPlatformName(platform.platform)}
                          </span>
                          {platform.hasExpiredTokens && (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                backgroundColor: '#fef3c7',
                                color: '#92400e',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                cursor: 'help'
                              }}
                              /*
                               * ⚠️ Le détail du refus était posé en `title` natif, et ce badge
                               * vit dans un `<table>` : la règle des infobulles du projet le
                               * dit — « le `title` ne sort pas sur les cellules de tableau ».
                               * Le message de la régie, celui qu'un support demandera, était
                               * donc illisible. On emprunte la bulle que ce tableau emploie
                               * déjà deux badges plus loin, plutôt que d'en monter une seconde.
                               */
                              onMouseEnter={survolerMention(
                                platform.expiredTokenMessage || 'Token expiré - reconnexion requise'
                              )}
                              onMouseLeave={() => setMentionSurvolee(null)}
                            >
                              ⚠️ {libelleRefusConnecteur(platform.expiredTokenMessage)}
                            </span>
                          )}
                          {platform.comptesNonMesures > 0 && (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                backgroundColor: '#fee2e2',
                                color: '#991b1b',
                                fontSize: '0.75rem',
                                fontWeight: '500',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                cursor: 'help'
                              }}
                              onMouseEnter={survolerMention(
                                `Les chiffres de cette ligne sont incomplets : ${platform.comptesNonMesures === 1 ? 'un compte n\'a pas pu être mesuré' : `${platform.comptesNonMesures} comptes n'ont pas pu être mesurés`} lors du dernier calcul, et compte${platform.comptesNonMesures === 1 ? '' : 's'} donc pour zéro.\n\n${platform.messageNonMesure}`
                              )}
                              onMouseLeave={() => setMentionSurvolee(null)}
                            >
                              ⚠️ {platform.comptesNonMesures === 1 ? '1 compte non mesuré' : `${platform.comptesNonMesures} comptes non mesurés`}
                            </span>
                          )}
                        </div>
                      </td>
                      {/* Cellules dynamiques selon la configuration des métriques */}
                      {platformsMetricsConfig.order.map((metricKey) => (
                        <td key={metricKey} style={{
                          padding: '16px 20px',
                          textAlign: 'center',
                          fontWeight: metricKey === 'cos' ? '600' : '500',
                          fontSize: '0.875rem',
                          color: '#374151'
                        }}>
                          <div>{cellule(metriquePlateforme(platform, metricKey))}</div>
                        </td>
                      ))}
                      <td style={{
                        padding: '16px 20px',
                        textAlign: 'center'
                      }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDashboard(getPlatformName(platform.platform));
                          }}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#1a1a1a',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#333333'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#1a1a1a'}
                        >
                          Voir
                        </button>
                      </td>
                    </tr>
                    
                    {/* Lignes détaillées des comptes */}
                    {isExpanded && accounts.map((account, accountIndex) => (
                      <tr
                        key={`${platform.platform}-${account.accountId || accountIndex}`}
                        className="homepage-table-row"
                        style={{
                          backgroundColor: 'white',
                          borderBottom: accountIndex === accounts.length - 1 && index < plateformesTriees.length - 1 ? '1px solid #f1f5f9' : '1px solid #e2e8f0'
                        }}
                      >
                        <td style={{ padding: '12px 20px 12px 60px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                            {/* Le tableau est en `table-layout: fixed` : sans césure, un
                                identifiant d'un seul tenant (`adacct_…` chez ChatGPT Ads)
                                déborde sur la colonne voisine au lieu de passer à la ligne. */}
                            <span style={{ fontWeight: '500', color: '#374151', fontSize: '0.875rem', overflowWrap: 'anywhere' }}>
                              {account.accountName || 'Compte sans nom'}
                            </span>
                            {account.siteName && (
                              <span style={{ color: '#64748b', fontSize: '0.75rem' }}>
                                Site: {account.siteName}
                              </span>
                            )}
                            {/* Les zéros de cette ligne ne sont pas une mesure : le dire là où
                                on les lit, et non seulement sur la ligne de la plateforme. */}
                            {account.mesureIndisponible && (
                              <span
                                style={{ color: '#991b1b', fontSize: '0.75rem', cursor: 'help' }}
                                onMouseEnter={survolerMention(account.mesureIndisponibleMessage
                                  || 'La régie n\'a pas répondu lors du dernier calcul.')}
                                onMouseLeave={() => setMentionSurvolee(null)}
                              >
                                ⚠️ Non mesuré — les zéros ci-contre ne sont pas une mesure
                              </span>
                            )}
                          </div>
                        </td>
                        {/* Cellules dynamiques selon la configuration des métriques */}
                        {platformsMetricsConfig.order.map((metricKey) => (
                          <td key={metricKey} style={{
                            padding: '12px 20px',
                            textAlign: 'center',
                            fontSize: '0.875rem',
                            fontWeight: metricKey === 'cos' ? '500' : 'normal',
                            color: metricKey === 'cout' ? '#6b7280' : '#374151'
                          }}>
                            <div>{cellule(metriqueCompte(account, metricKey))}</div>
                          </td>
                        ))}
                        <td style={{
                          padding: '12px 20px',
                          textAlign: 'center'
                        }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('🔍 [HOMEPAGE CLICK] Compte cliqué:', {
                                fullAccount: account,
                                accountId: account.accountId,
                                id: account.id,
                                sourceId: account.sourceId,
                                platformType: platform.platform
                              });
                              console.log('🔥 [FACEBOOK DEBUG] Envoi vers dashboard avec:', {
                                accountId: account.sourceId || account.accountId || account.id,
                                accountType: platform.platform
                              });
                              // Utiliser sourceId comme identifiant car c'est ce que le dashboard utilise
                              handleViewAccountDashboard({
                                accountId: account.sourceId || account.accountId || account.id,
                                accountType: platform.platform
                              });
                            }}
                            style={{
                              padding: '4px 8px',
                              backgroundColor: '#1a1a1a',
                              color: 'white',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              fontSize: '0.7rem',
                              fontWeight: '500',
                              transition: 'background-color 0.2s ease'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#333333'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#1a1a1a'}
                          >
                            Voir
                          </button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
            {platformData.length > 1 && (
            <tfoot>
              <tr style={{
                backgroundColor: '#f9f7f4',
                borderTop: '2px solid #e2e8f0',
                fontWeight: '600'
              }}>
                <td style={{ padding: '16px 20px', fontWeight: '700', color: '#1e293b', fontSize: '0.875rem' }}>
                  Budget total
                </td>
                {platformsMetricsConfig.order.map((metricKey) => (
                  <td key={metricKey} style={{
                    padding: '16px 20px',
                    textAlign: 'center',
                    fontWeight: metricKey === 'cout' ? '600' : 'normal',
                    color: metricKey === 'cout' ? '#1e293b' : '#94a3b8',
                    fontSize: '0.875rem'
                  }}>
                    {metricKey === 'cout' ? (
                      <div>
                        {formatCurrency(totals.totalCost)}
                        {formatGrowthRate(totals.growthRates?.cost, 'cost')}
                      </div>
                    ) : '-'}
                  </td>
                ))}
                <td style={{ padding: '16px 20px', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' }}>-</td>
              </tr>
            </tfoot>
            )}
          </table>
        </div>
      )}

      {/*
        Les dashboards sur mesure, sous les deux tableaux.

        ⚠️ **La liste arrive déjà bornée par le serveur** — périmètre, droit client et
        `visibleClient` compris. On n'y ajoute aucun filtre de droit ici : il serait le
        quatrième, et le premier à diverger. Seul `actif` est retenu, parce qu'une fiche
        désactivée existe toujours côté agence sans devoir s'ouvrir.

        ⚠️ Rien ne s'affiche s'il n'y en a aucun : un titre de section au-dessus du vide se lit
        comme un chargement qui a échoué — c'est la même règle que pour « Analytics » dans la
        barre latérale et l'écran de choix.
      */}
      {dashboardsSurMesureVisibles.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <LayoutDashboard size={18} style={{ color: '#64748b' }} />
            <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: '600', color: '#1e293b' }}>
              Dashboards sur mesure
            </h3>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: '16px'
          }}>
            {dashboardsSurMesureVisibles.map(dashboard => {
              /*
               * ⚠️ `icone` est un **nom** d'icône (`'Building2'`), pas un emoji : rendu tel
               * quel, il écrivait « Building2 » en toutes lettres dans la carte, débordant de
               * son carré. La table le résout, et sert aussi la liste de la modale.
               */
              const Icone = iconeDashboard(dashboard.icone);
              /* Le sous-titre s'il y en a un, sinon le compte : deux fiches peuvent porter le
                 même nom sur deux comptes, la carte doit dire de laquelle elle parle. */
              const legende = dashboard.sousTitre || dashboard.compte?.name || '';

              return (
                <button
                  key={dashboard.id}
                  onClick={() => ouvrirDashboardSurMesure?.(dashboard.id)}
                  title={legende ? `${dashboard.nom} — ${legende}` : dashboard.nom}
                  className="dashboard-selection-card"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '14px',
                    padding: '18px 20px', textAlign: 'left',
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0', borderRadius: '12px',
                    cursor: 'pointer', width: '100%',
                    /* ⚠️ Sans cela, la grille étire les cartes à la hauteur de la plus haute :
                       une légende sur deux lignes grandirait toute la rangée. */
                    minWidth: 0
                  }}
                >
                  <span style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    width: '40px', height: '40px', flexShrink: 0,
                    backgroundColor: '#f1f5f9', borderRadius: '10px'
                  }}>
                    <Icone size={20} style={{ color: '#475569' }} />
                  </span>

                  {/* ⚠️ `minWidth: 0` sur le conteneur de texte : sans lui, un élément flex ne
                      descend jamais sous la largeur de son contenu, et la troncature
                      n'arriverait jamais — c'est la carte qui déborderait. */}
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{
                      display: 'block', fontSize: '0.95rem', fontWeight: '600', color: '#1e293b',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                    }}>
                      {dashboard.nom}
                    </span>
                    {legende && (
                      <span style={{
                        display: 'block', marginTop: '3px', fontSize: '0.8rem', color: '#64748b',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                      }}>
                        {legende}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Modals de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showAnalyticsMetricsConfig}
        onClose={() => setShowAnalyticsMetricsConfig(false)}
        componentId="homepage_analytics"
        accountId="global"
        componentTitle="Tableau Analytics"
        hideApplyToAll={true}
      />
      <MetricsConfigModal
        isOpen={showPlatformsMetricsConfig}
        onClose={() => setShowPlatformsMetricsConfig(false)}
        componentId="homepage_platforms"
        accountId="global"
        componentTitle="Tableau Publicité"
        hideApplyToAll={true}
      />

      {/* D'où vient le nombre survolé. Par portail : les tableaux rognent leurs lignes. */}
      {mentionSurvolee && ReactDOM.createPortal(
        <BulleSurvol position={mentionSurvolee}>
          {/* `pre-line` : les mentions d'une seule phrase n'en souffrent pas, et les listes de
              sources de conversion ont besoin d'une ligne par source. */}
          <div style={{
            padding: '4px 6px',
            fontSize: '12px',
            color: '#4b5563',
            lineHeight: 1.5,
            whiteSpace: 'pre-line'
          }}>
            {mentionSurvolee.texte}
          </div>
        </BulleSurvol>,
        document.body
      )}
    </div>
  );
};

export default HomePage;